import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip as BsTooltip } from "react-bootstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { CircleMarker, LayerGroup, LayersControl, MapContainer, Marker, Polygon, Polyline, Popup, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import { LatLngTuple } from "leaflet";
import * as Leaflet from "leaflet";
import { IfrRouteResult, IfrRouteFound, IfrRouteDelay, IfrRouteWaypoint, IfrPlanningOverrides, ValidateFplResponse } from "../avitracerApi";
import { FlightFplFile } from "../models/flight";
import { CupPoint, asIfrFix, getAllCupPoints, getLatLngTupleForCupPoint, iconUrlForCupPoint, typeForCupPoint } from "../utils/CupPointUtils";
import VisibleCupPoints, { OverlayToggleSync, ZoomToSeePointsHint } from "./VisibleCupPoints";
import ifrAirwaysData from "../data/ifrAirwaysLG.json";
import RulesTypeBadge from "./RulesTypeBadge";
import "../css/IfrRoutePlanner.css";

//ATS route network parsed from AIP Greece ENR 3 (scripts/parseEnr3Airways.js in the backend repo).
type AirwayFix = { ident: string; name: string; lat: number; lon: number; firBoundary?: boolean };
type AirwayDef = { idents: string[]; points: AirwayFix[] };
const AIRWAYS: AirwayDef[] = (ifrAirwaysData as { airways: AirwayDef[] }).airways;

type AirwaySegment = { airway: AirwayDef; from: AirwayFix; to: AirwayFix };
const AIRWAY_SEGMENTS: AirwaySegment[] = AIRWAYS.flatMap((airway) =>
  airway.points.slice(0, -1).map((from, i) => ({ airway, from, to: airway.points[i + 1] }))
);
const AIRWAY_FIXES: AirwayFix[] = Array.from(
  new Map(AIRWAYS.flatMap((a) => a.points).map((p) => [p.ident, p])).values()
);
const FIX_BY_IDENT = new Map(AIRWAY_FIXES.map((f) => [f.ident, f]));
const AIRWAYS_BY_IDENT = new Map<string, AirwayDef[]>();
for (const a of AIRWAYS) for (const id of a.idents) AIRWAYS_BY_IDENT.set(id, [...(AIRWAYS_BY_IDENT.get(id) ?? []), a]);

//The fixes of `awy` between two idents (either direction), or undefined when not both on the airway.
function airwayPathBetween(awy: AirwayDef, fromIdent: string, toIdent: string): AirwayFix[] | undefined {
  const i = awy.points.findIndex((p) => p.ident === fromIdent);
  const j = awy.points.findIndex((p) => p.ident === toIdent);
  if (i < 0 || j < 0 || i === j) return undefined;
  return i < j ? awy.points.slice(i, j + 1) : awy.points.slice(j, i + 1).reverse();
}

type DraftPoint = { ident: string; lat: number; lon: number; ifr?: boolean };

//---- airway network graph (built once) for offline fix-to-fix auto-routing ----
type NetEdge = { to: string; dist: number; awy: AirwayDef };
const NETWORK = new Map<string, NetEdge[]>();
{
  const gcDistNm = (a: AirwayFix, b: AirwayFix): number => {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad;
    const dLon = (b.lon - a.lon) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
    return 3440 * 2 * Math.asin(Math.sqrt(h));
  };
  const addEdge = (from: AirwayFix, to: AirwayFix, awy: AirwayDef) => {
    const list = NETWORK.get(from.ident) ?? [];
    list.push({ to: to.ident, dist: gcDistNm(from, to), awy });
    NETWORK.set(from.ident, list);
  };
  for (const awy of AIRWAYS) {
    for (let i = 0; i < awy.points.length - 1; i++) {
      addEdge(awy.points[i], awy.points[i + 1], awy);
      addEdge(awy.points[i + 1], awy.points[i], awy);
    }
  }
}

//ICAO RNAV route designator letters (Annex 11): L M N P Q T Y Z (with U- upper variants).
const RNAV_LETTERS = new Set(["L", "M", "N", "P", "Q", "T", "Y", "Z"]);
function isRnavAirway(awy: AirwayDef): boolean {
  const base = awy.idents.find((i) => !i.startsWith("U")) ?? awy.idents[0];
  return RNAV_LETTERS.has(base[0]);
}

//Shortest along-airway path between two fixes across the whole network (Dijkstra with a small
//penalty for switching airways, so parallel airways don't interleave, and a hair-line penalty for
//conventional airways so ties between parallel routes resolve to the RNAV one — e.g. ROPOX-SKP
//exists on both B1 and M749; RNAV M749 is what an RNAV-equipped flight would normally file).
//Returns the path compressed to Field-15 form: one segment per airway run ending at its exit fix.
function findNetworkPath(from: string, to: string): { awy: AirwayDef; exit: string }[] | undefined {
  if (from === to || !NETWORK.has(from) || !NETWORK.has(to)) return undefined;

  const cost = new Map<string, number>([[from, 0]]);
  const prev = new Map<string, { fix: string; awy: AirwayDef }>();
  const done = new Set<string>();
  const queue: [number, string][] = [[0, from]];

  while (queue.length > 0) {
    let qi = 0;
    for (let i = 1; i < queue.length; i++) if (queue[i][0] < queue[qi][0]) qi = i;
    const [c, fix] = queue.splice(qi, 1)[0];
    if (done.has(fix)) continue;
    done.add(fix);
    if (fix === to) break;
    const inAwy = prev.get(fix)?.awy;
    for (const e of NETWORK.get(fix) ?? []) {
      if (done.has(e.to)) continue;
      const nc = c + e.dist + (inAwy && e.awy !== inAwy ? 5 : 0) + (isRnavAirway(e.awy) ? 0 : 0.01);
      if (nc < (cost.get(e.to) ?? Infinity)) {
        cost.set(e.to, nc);
        prev.set(e.to, { fix, awy: e.awy });
        queue.push([nc, e.to]);
      }
    }
  }

  if (!prev.has(to)) return undefined;

  //walk back, then compress consecutive same-airway edges
  const edges: { awy: AirwayDef; toFix: string }[] = [];
  let cur = to;
  while (cur !== from) {
    const p = prev.get(cur)!;
    edges.unshift({ awy: p.awy, toFix: cur });
    cur = p.fix;
  }
  if (edges.length > 60) return undefined; //sanity cap
  const segments: { awy: AirwayDef; exit: string }[] = [];
  for (const e of edges) {
    const lastSeg = segments.at(-1);
    if (lastSeg && lastSeg.awy === e.awy) lastSeg.exit = e.toFix;
    else segments.push({ awy: e.awy, exit: e.toFix });
  }
  return segments;
}

type RouteSuggestion = { ident: string; kind: "fix" | "awy" | "apt" | "vfr" | "nav" };

//Context-aware completions for the route box: after a fix — the airways through it (and fixes);
//after "FIX AWY" — the exit fixes along that airway; otherwise matching fixes/airports. Inside a
//VFR segment of a mixed (Y/Z) route, visual reporting points + aerodromes replace the airway world.
//Free suggestions sort CLOSEST FIRST, measured from the last locatable point before the caret
//(falling back to the departure aerodrome).
function computeRouteSuggestions(
  text: string,
  airports: string[],
  vfrPoints?: string[],
  inVfrSegment?: boolean,
  resolvePoint?: (ident: string) => DraftPoint | undefined,
  departureIdent?: string,
  navaids?: string[]
): RouteSuggestion[] {
  const endsWithSpace = /\s$/.test(text) || text.length === 0;
  const tokens = text.toUpperCase().trim().split(/\s+/).filter((t) => t.length > 0);
  const partial = endsWithSpace ? "" : tokens.pop() ?? "";
  const last = tokens.at(-1);
  const prev = tokens.at(-2);

  const resolveAny = (ident: string): { lat: number; lon: number } | undefined => {
    const m = ident.match(POINT_CHANGE_RE);
    const id = m ? m[1] : ident;
    return FIX_BY_IDENT.get(id) ?? resolvePoint?.(id);
  };

  //reference position: last locatable route point before the caret, else the departure aerodrome
  let refPos: { lat: number; lon: number } | undefined;
  for (let i = tokens.length - 1; i >= 0 && !refPos; i--) refPos = resolveAny(tokens[i]);
  if (!refPos && departureIdent) refPos = resolveAny(departureIdent);

  const dist2 = (ident: string): number => {
    const p = resolveAny(ident);
    if (!p || !refPos) return Number.MAX_VALUE;
    const dLat = p.lat - refPos.lat;
    const dLon = (p.lon - refPos.lon) * Math.cos((refPos.lat * Math.PI) / 180);
    return dLat * dLat + dLon * dLon;
  };
  const byDistance = (idents: string[]): string[] => (refPos ? [...idents].sort((a, b) => dist2(a) - dist2(b)) : idents);

  //never suggest the point the caret sits on — it is trivially the closest and never the next leg
  //(earlier points stay suggestible: round-robin routes legitimately revisit them). Skip trailing
  //IFR/VFR markers and reduce a rules-change group ("AMALA/N0120F080") to its point.
  let li = tokens.length - 1;
  while (li >= 0 && (tokens[li] === "IFR" || tokens[li] === "VFR")) li--;
  const lastToken = li >= 0 ? tokens[li] : undefined;
  const lastUsed = lastToken ? lastToken.match(POINT_CHANGE_RE)?.[1] ?? lastToken : undefined;

  const out: RouteSuggestion[] = [];
  const seen = new Set<string>();
  const push = (ident: string, kind: RouteSuggestion["kind"]) => {
    if (ident === lastUsed) return;
    if (!seen.has(ident) && ident.startsWith(partial) && ident !== partial) {
      seen.add(ident);
      out.push({ ident, kind });
    }
  };

  if (inVfrSegment) {
    //reporting points and radio navaids (VFR navigation by VOR/NDB is standard) ranked together
    const pool = [
      ...(vfrPoints ?? []).map((v) => ({ ident: v, kind: "vfr" as const })),
      ...(navaids ?? []).map((n) => ({ ident: n, kind: "nav" as const })),
    ];
    const sorted = refPos ? [...pool].sort((a, b) => dist2(a.ident) - dist2(b.ident)) : pool;
    for (const c of sorted) push(c.ident, c.kind);
    for (const a of byDistance(airports)) push(a, "apt");
    return out.slice(0, 8);
  }

  const lastAirways = last ? AIRWAYS_BY_IDENT.get(last) : undefined;
  if (lastAirways && prev && FIX_BY_IDENT.has(prev)) {
    //"... FIX AWY |" -> exit fixes along that airway, nearest first
    for (const awy of lastAirways) {
      const i = awy.points.findIndex((pt) => pt.ident === prev);
      if (i < 0) continue;
      const ordered: AirwayFix[] = [];
      for (let d = 1; d < awy.points.length; d++) {
        if (i + d < awy.points.length) ordered.push(awy.points[i + d]);
        if (i - d >= 0) ordered.push(awy.points[i - d]);
      }
      ordered.forEach((pt) => push(pt.ident, "fix"));
    }
  } else if (last && FIX_BY_IDENT.has(last)) {
    //"... FIX |" -> airways through that fix first, then the closest other fixes
    for (const [ident, defs] of Array.from(AIRWAYS_BY_IDENT.entries())) {
      if (defs.some((a) => a.points.some((pt) => pt.ident === last))) push(ident, "awy");
    }
    for (const f of byDistance(AIRWAY_FIXES.map((x) => x.ident))) push(f, "fix");
  } else {
    for (const f of byDistance(AIRWAY_FIXES.map((x) => x.ident))) push(f, "fix");
    for (const a of byDistance(airports)) push(a, "apt");
  }

  return out.slice(0, 8);
}

//Mixed-rules (Y/Z) Field-15 meta tokens: speed/level groups may end in "VFR" (Z initial level),
//"IFR"/"VFR" mark rules changes, and "POINT/N0120F080" attaches a speed/level change to a point.
const SPEED_LEVEL_RE = /^[NMK]\d{4}(?:VFR|[FASM]\d{3,4})$/;
const POINT_CHANGE_RE = /^([A-Z0-9]{2,5})\/[NMK]\d{4}(?:VFR|[FASM]\d{3,4})$/;

//A mixed-rules (Y/Z) Field-15 string carries at least one bare IFR/VFR change marker.
function isMixedField15(field15: string): boolean {
  return / (IFR|VFR) /.test(` ${field15.toUpperCase().trim()} `);
}

//Last route token that can anchor an airway connection: skips trailing IFR markers (the airway leg
//starts AT the pick-up fix) and reduces rules-change groups to their point. Returns undefined when
//the route ends in the VFR world (a VFR marker or a non-fix point) — no airway connects there.
function lastConnectableFix(text: string): string | undefined {
  const parts = text.toUpperCase().trim().split(/\s+/).filter((x) => x.length > 0);
  let i = parts.length - 1;
  while (i >= 0 && parts[i] === "IFR") i--;
  if (i < 0) return undefined;
  const m = parts[i].match(POINT_CHANGE_RE);
  const ident = m ? m[1] : parts[i];
  return FIX_BY_IDENT.has(ident) ? ident : undefined;
}

//Live preview of the manual-route text: resolves fix/point idents to coordinates and expands
//"FIX AWY FIX" triples along the airway's real path. Rules-change tokens reduce to their point
//("SOSUS/N0120F080" plots as SOSUS); unknown tokens are simply skipped. Each point carries the
//rules of the leg ARRIVING at it (mixed Y/Z plans render VFR legs dashed on the map).
//Some VFR reporting points are named in words ("EAST AIGINA"). Field 15 is space-delimited, so such a
//name would tokenise into two words that resolve to nothing. Inside the planner the words are joined by
//a sentinel so the name behaves as ONE token everywhere — one chip, one ×, one caret stop — and the
//sentinel becomes a real space again the moment the string leaves the planner, so what is filed is
//"EAST AIGINA", exactly what a VFR route files today.
const PHRASE_JOIN = "␟";
const PHRASE_JOIN_RE = new RegExp(PHRASE_JOIN, "g");

//Sentinel form -> what a human reads (chips, tooltips) and what gets filed.
function phraseToDisplay(token: string): string {
  return token.replace(PHRASE_JOIN_RE, " ");
}

//Real spaces -> sentinel form, for the known multi-word names only. Whole-token matches only, so a
//phrase can never be glued onto part of a neighbouring designator.
function joinKnownPhrases(text: string, phrases: string[]): string {
  let s = text;
  for (const phrase of phrases) {
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    s = s.replace(new RegExp(`(^|\\s)${escaped}(?=\\s|$)`, "g"), (_m, pre) => pre + phrase.split(" ").join(PHRASE_JOIN));
  }
  return s;
}

function computeDraftRoute(
  text: string,
  resolveExtra: (ident: string) => DraftPoint | undefined,
  flightRules?: "I" | "Y" | "Z" | "V",
  //multi-word VFR point names to re-join before tokenising, so they resolve as one point
  phrases?: string[]
): DraftPoint[] {
  text = phrases && phrases.length > 0 ? joinKnownPhrases(text.toUpperCase(), phrases) : text;
  const tokens = text
    .toUpperCase()
    .split(/\s+/)
    .map((t) => {
      const change = t.match(POINT_CHANGE_RE);
      return change ? change[1] : t;
    })
    .filter((t) => t.length > 0 && t !== "DCT" && !SPEED_LEVEL_RE.test(t));

  //Initial rules from the first change marker: a route whose first marker is "IFR" started VFR (Z);
  //first marker "VFR" started IFR (Y). With no marker yet — a Z route still being built, before the
  //IFR pick-up is added — fall back to the flight rules: Z departs VFR, everything else departs IFR.
  const firstRulesToken = tokens.find((t) => t === "IFR" || t === "VFR");
  let ifrNow = firstRulesToken ? firstRulesToken !== "IFR" : flightRules !== "Z";

  const resolve = (t: string): DraftPoint | undefined => {
    const fix = FIX_BY_IDENT.get(t);
    if (fix) return { ident: fix.ident, lat: fix.lat, lon: fix.lon };
    return resolveExtra(t);
  };

  const pts: DraftPoint[] = [];
  for (let k = 0; k < tokens.length; k++) {
    const t = tokens[k];
    if (t === "IFR") {
      ifrNow = true;
      continue;
    }
    if (t === "VFR") {
      ifrNow = false;
      continue;
    }
    const airways = AIRWAYS_BY_IDENT.get(t);
    if (airways && pts.length > 0 && k + 1 < tokens.length) {
      const prev = pts[pts.length - 1];
      let expanded: AirwayFix[] | undefined;
      for (const awy of airways) {
        expanded = airwayPathBetween(awy, prev.ident, tokens[k + 1]);
        if (expanded) break;
      }
      if (expanded) {
        pts.push(...expanded.slice(1).map((f) => ({ ident: f.ident, lat: f.lat, lon: f.lon, ifr: ifrNow })));
        k++;
        continue;
      }
      continue;
    }
    const r = resolve(t);
    if (r) pts.push({ ...r, ifr: ifrNow });
  }
  return pts;
}

//fplFile is absent when selection only updates the (unsaved) form rather than persisting server-side.
export type IfrRouteSelectResult = { fplFile?: FlightFplFile; ifpsRoute?: string };

const ALL_OPTIMIZES = ["time", "fuel", "preferred"];
const OPTIMIZE_LABELS: { [key: string]: string } = { time: "Time", fuel: "Fuel", preferred: "Preferred", manual: "Manual", current: "Current" };
const LEVEL_CHOICES = [40, 60, 80, 100, 120, 140, 160, 180];
//Each optimize × level combination is a separate autorouter run — keep the fan-out gentle.
const MAX_COMBOS = 6;

// EFIS-flavored candidate colors; magenta is reserved for the armed (selected) route.
const ROUTE_COLORS = ["#22c3e6", "#ffb02e", "#8f7bff", "#35d27a", "#ff7a59"];
const ARMED_COLOR = "#f042e6";

function optimizeLabel(key: string): string {
  return OPTIMIZE_LABELS[key] ?? key;
}

function flLabel(level?: number): string | undefined {
  return level !== undefined && level > 0 ? `FL${String(level).padStart(3, "0")}` : undefined;
}

function ifpuvIsValid(validation?: string): boolean {
  return (validation ?? "").trim().toUpperCase().startsWith("NO ERROR");
}

function formatSecondsAsHHMM(seconds?: number): string | undefined {
  if (seconds === undefined) return undefined;
  const totalMins = Math.round(seconds / 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function formatEpochWindow(startTime?: number, endTime?: number): string | undefined {
  if (startTime === undefined && endTime === undefined) return undefined;
  const s = startTime !== undefined ? moment.unix(startTime).utc().format("DD/MM HH:mm") : "?";
  const e = endTime !== undefined ? moment.unix(endTime).utc().format("HH:mm") : "?";
  return `${s}–${e}Z`;
}

//GeoJSON (Multi)Polygon -> leaflet ring positions, one entry per polygon.
function geometryPolygons(geometry: any): LatLngTuple[][][] {
  if (!geometry) return [];
  const ringToLatLng = (ring: [number, number][]) => ring.map(([lon, lat]) => [lat, lon] as LatLngTuple);
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.map((poly: [number, number][][]) => poly.map(ringToLatLng));
  }
  if (geometry.type === "Polygon") {
    return [geometry.coordinates.map(ringToLatLng)];
  }
  return [];
}

function waypointPositions(waypoints: IfrRouteWaypoint[]): LatLngTuple[] {
  return waypoints.filter((w) => w.lat !== undefined && w.lon !== undefined).map((w) => [w.lat!, w.lon!] as LatLngTuple);
}

//ATFM delay band from autorouter's per-regulation code, matching its site legend
//(blue = expected < 15 min, orange = expected < 45 min).
function delayBand(code?: string): { label: string; minor: boolean } {
  switch ((code ?? "").toUpperCase()) {
    case "L":
      return { label: "expected delay < 15 min", minor: true };
    case "M":
      return { label: "expected delay < 45 min", minor: false };
    case "H":
      return { label: "expected delay > 45 min", minor: false };
    default:
      return { label: code ? `expected delay (band ${code})` : "expected delay", minor: false };
  }
}

//Does the regulation's active window intersect the planned flight window (ETD .. ETD + EET)?
function delayAffectsFlight(d: IfrRouteDelay, departureDateTime?: Date, routeTimeSeconds?: number): boolean | undefined {
  if (!departureDateTime || isNaN(departureDateTime.getTime()) || d.startTime === undefined || d.endTime === undefined) {
    return undefined;
  }
  const etd = departureDateTime.getTime() / 1000;
  const eta = etd + (routeTimeSeconds ?? 0);
  return d.startTime <= eta && d.endTime >= etd;
}

type RouteOption = { key: string; result: IfrRouteFound; optimizes: string[] };

interface PlanIfrRouteModalProps {
  show: boolean;
  onHide: () => void;
  isIfr?: boolean;
  ifpsRoute?: string;
  baseLevel?: number;
  //mixed-rules context: Y/Z reveal the rules-change helper chips in the manual builder
  flightRules?: "I" | "Y" | "Z" | "V";
  //used to compose the IFR pick-up group ("SOSUS/N0120F080") together with the FL input
  cruiseSpeedKt?: number;
  //planned off-block time — enables the "affects your ETD / outside your times" indicator on ATFM delays
  departureDateTime?: Date;
  //initial planning inputs (editable inside the planner; changes affect planning only, not the saved entity)
  departure?: string;
  destination?: string;
  alternate1?: string;
  alternate2?: string;
  findIfrRoutesAction?: (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides) => Promise<IfrRouteResult[]>;
  //checks a user-written Field-15 route verbatim; the result joins the option list like any other
  checkManualRouteAction?: (field15: string, overrides?: IfrPlanningOverrides) => Promise<IfrRouteResult>;
  selectIfrRouteAction?: (field15: string, overrides?: IfrPlanningOverrides) => Promise<IfrRouteSelectResult>;
  validateFplAction?: () => Promise<ValidateFplResponse>;
  onRouteSelected: (result: IfrRouteSelectResult) => void;
  //false when selection only sets a form value (creation form) instead of persisting server-side.
  persistedOnSelect?: boolean;
  //"route" when planning a saved route template, where no flight exists — relabels the setup card and
  //says which inputs are stored on the route vs. used for this planning run only.
  entityKind?: "flight" | "route";
  //Fired with the FULL armed option when a route is selected. Flights don't need it (the backend
  //re-derives geometry from the persisted Field 15), but a route template has no backend to re-derive
  //from — it uses this to keep the resolved waypoints + IFPUV result instead of asking autorouter for
  //the identical answer again at save time.
  onRouteArmed?: (armed: IfrRouteFound) => void;
}

// Fullscreen "dispatch console" for IFR route planning: candidate routes are drawn over the
// aviation chart, ATFM regulation areas overlay in amber, and each option reads as a flight
// strip in the left rail. Autorouter returns one best route per optimize × level combination, so
// the option space is explored by toggling profiles and flight levels; each profile's results
// stream in as its run completes. Selecting arms a route (magenta, marching dashes).
function PlanIfrRouteModal({ show, onHide, isIfr, ifpsRoute, baseLevel, flightRules, cruiseSpeedKt, departureDateTime, departure, destination, alternate1, alternate2, findIfrRoutesAction, checkManualRouteAction, selectIfrRouteAction, validateFplAction, onRouteSelected, persistedOnSelect = true, entityKind = "flight", onRouteArmed }: PlanIfrRouteModalProps) {
  //"preferred" (EUROCONTROL route-charge-optimal) is the sensible default; time/fuel are opt-in extras
  const [optimizes, setOptimizes] = useState<string[]>(["preferred"]);
  const [selectedLevels, setSelectedLevels] = useState<number[]>(baseLevel && baseLevel > 0 ? [baseLevel] : []);
  //user-typed flight levels beyond the standard choices (join the chip row and are selectable like any other)
  const [customLevels, setCustomLevels] = useState<number[]>([]);
  const [customLevelText, setCustomLevelText] = useState("");
  const [finding, setFinding] = useState(false);
  const [results, setResults] = useState<IfrRouteResult[] | undefined>(undefined);
  const [selectingField15, setSelectingField15] = useState<string | undefined>(undefined);
  const [selectedField15, setSelectedField15] = useState<string | undefined>(ifpsRoute);
  //route the planner opened with (if any) — used to offer "revert" when closing after a disarm
  const initialArmedRoute = useRef<string | undefined>(ifpsRoute && ifpsRoute.trim().length > 0 ? ifpsRoute : undefined);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hoverKey, setHoverKey] = useState<string | undefined>(undefined);
  const [pinnedKey, setPinnedKey] = useState<string | undefined>(undefined);
  //accordion: the one option row whose details are open (all rows collapse to one line by default)
  const [expandedKey, setExpandedKey] = useState<string | undefined>(undefined);
  //planning-input snapshot each result was validated with — when the inputs change afterwards,
  //the affected rows flag as outdated instead of keeping a misleading "OK" badge
  const [optionSignatures, setOptionSignatures] = useState<{ [key: string]: string }>({});
  const [expandedWaypoints, setExpandedWaypoints] = useState<{ [key: string]: boolean }>({});
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidateFplResponse["validation"] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  //The manual route is split around a movable caret: `manualRoute` holds the tokens BEFORE the
  //caret (plus the token being typed), `manualTail` the tokens AFTER it. The inline input renders
  //between the two, so clicking a pill (or ←/→) moves the insertion point anywhere in the route.
  const [manualRoute, setManualRoute] = useState("");
  const [manualTail, setManualTail] = useState("");
  //free-text mode: the pill editor swaps for a plain textarea holding the raw Field-15 string
  const [textMode, setTextMode] = useState(false);
  const [textDraft, setTextDraft] = useState("");
  const [checkingManual, setCheckingManual] = useState(false);
  //the airway pill whose alternatives panel is open (head = before the caret, tail = after)
  const [openAirwayToken, setOpenAirwayToken] = useState<{ side: "head" | "tail"; idx: number } | undefined>(undefined);
  //mixed-rules: the IFR pick-up editor ("SOSUS/N0120F080 IFR") — undefined = closed
  const [pickupDraft, setPickupDraft] = useState<{ point: string; speed: string; level: string } | undefined>(undefined);
  //the two planning workflows are mutually exclusive in the UI: autorouter find vs manual build
  //Mixed-rules (Y/Z) plans are built manually only: autorouter route-find solves pure IFR
  //aerodrome-to-aerodrome routes and cannot produce VFR legs or the rules-change structure.
  const isMixedRules = flightRules === "Y" || flightRules === "Z";
  const [mode, setMode] = useState<"find" | "manual">(isMixedRules ? "manual" : "find");
  //when a route is already checked and the editor is empty, the build box collapses to a button so
  //the checked route gets the focus; expanding (or copying a route in) opens the builder again
  const [builderExpanded, setBuilderExpanded] = useState(false);
  //first-open setup: without DEP/DEST there is nothing to plan — ask for the airports first and
  //reveal the route tools after. Flight context arrives with both set, so nothing changes there.
  const [setupDone, setSetupDone] = useState(
    () => /^[A-Z]{4}$/.test((departure ?? "").toUpperCase().trim()) && /^[A-Z]{4}$/.test((destination ?? "").toUpperCase().trim())
  );
  const manualInputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<Leaflet.Map | null>(null);
  //Map layers follow the section being edited: IFR points (airways ▲) in the IFR section, VFR
  //reporting points in the VFR section. Both are user-toggleable from the layers control.
  const [vfrLayerVisible, setVfrLayerVisible] = useState(isMixedRules);
  const [airwaysLayerVisible, setAirwaysLayerVisible] = useState(true);
  const [navPoints, setNavPoints] = useState<CupPoint[]>([]);

  //Nav points (airports / VOR / NDB / reporting points) for the map layers — same CUP database the
  //VFR route map uses; clicking one can append its ident to the manual route.
  useEffect(() => {
    getAllCupPoints().then(setNavPoints);
  }, []);
  const navPointsByLayer = useMemo(() => {
    const types = navPoints.map(typeForCupPoint);
    return {
      airports: navPoints.filter((_, i) => types[i] === "Airport"),
      navaids: navPoints.filter((_, i) => types[i] === "VOR" || types[i] === "NDB"),
      ifrFixes: navPoints.filter((_, i) => types[i] === "IFR Fix"),
      //VFR points that are also IFR fixes — drawn as IFR fixes while the VFR layer is hidden
      vfrIfrFixes: navPoints.filter((p) => p.isIfrFix).map(asIfrFix),
      reportingPoints: navPoints.filter((_, i) => types[i] !== "Airport" && types[i] !== "VOR" && types[i] !== "NDB" && types[i] !== "IFR Fix"),
    };
  }, [navPoints]);


  //Editable planning inputs — affect THIS planning session only (the saved entity is not modified).
  const [planDep, setPlanDep] = useState((departure ?? "").toUpperCase());
  const [planDest, setPlanDest] = useState((destination ?? "").toUpperCase());
  const [planAltn1, setPlanAltn1] = useState((alternate1 ?? "").toUpperCase());
  const [planAltn2, setPlanAltn2] = useState((alternate2 ?? "").toUpperCase());
  const [planDate, setPlanDate] = useState(departureDateTime ? moment(departureDateTime).format("YYYY-MM-DDTHH:mm") : "");
  const [planFL, setPlanFL] = useState(baseLevel && baseLevel > 0 ? String(baseLevel) : "");
  //The backend rejects a route-find/check with no usable cruise level ("IFR route-find needs an IFR
  //cruise level (e.g. FL080)."). Point the message at the field it is about by outlining the FL input.
  const levelErrorShown = !!error && /cruise level|flight level/i.test(error);
  const [currentRouteOption, setCurrentRouteOption] = useState<IfrRouteResult | undefined>(undefined);
  const [currentSeeded, setCurrentSeeded] = useState(false);

  //Preserve the already-selected route on re-plan: check it once and keep it as a "Current" option
  //card, so finding new routes never loses it. The check runs with the planning inputs (DEP/DEST/
  //level etc.) so it also works where those come from the planner alone (route templates).
  useEffect(() => {
    if (!show || !setupDone || currentSeeded || !ifpsRoute || !checkManualRouteAction) return;
    setCurrentSeeded(true);
    checkManualRouteAction(ifpsRoute, planningOverrides())
      .then((r) => {
        if (r.status === "found") {
          const cur: IfrRouteResult = { ...r, optimize: "current" };
          setCurrentRouteOption(cur);
          setResults((prev) => (prev === undefined ? [cur] : [cur, ...prev]));
          recordOptionSignatures([cur], planningSignature);
        }
      })
      .catch(() => { /* current route could not be re-checked — planning continues without it */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, setupDone, currentSeeded, ifpsRoute, checkManualRouteAction]);

  //FL is deliberately excluded: every result carries its own explicit level, so a changed FL input
  //does not invalidate options computed at other levels.
  const planningSignature = [planDep.trim(), planDest.trim(), planAltn1.trim(), planAltn2.trim(), planDate].join("|");

  const recordOptionSignatures = (rs: IfrRouteResult[], signature: string) => {
    setOptionSignatures((prev) => {
      const next = { ...prev };
      for (const r of rs) {
        if (r.status === "found") next[`${r.field15}|${r.level ?? ""}`] = signature;
      }
      return next;
    });
  };

  const planningOverrides = (): IfrPlanningOverrides => ({
    departure: planDep.trim() || undefined,
    destination: planDest.trim() || undefined,
    alternate1: planAltn1.trim() || undefined,
    alternate2: planAltn2.trim() || undefined,
    departureDateTime: planDate ? new Date(planDate) : undefined,
    level: Number(planFL) > 0 ? Number(planFL) : undefined,
  });

  const options = useMemo<RouteOption[]>(() => {
    const found = (results ?? []).filter((r): r is IfrRouteFound => r.status === "found");
    const dedup = new Map<string, RouteOption>();
    for (const r of found) {
      const key = `${r.field15}|${r.level ?? ""}`;
      const existing = dedup.get(key);
      if (existing) {
        if (!existing.optimizes.includes(r.optimize)) existing.optimizes.push(r.optimize);
      } else {
        dedup.set(key, { key, result: r, optimizes: [r.optimize] });
      }
    }
    return Array.from(dedup.values());
  }, [results]);

  const cupByCode = useMemo(() => {
    //airports + radio navaids + AIP ENR 4.4 IFR fixes — CUP "Waypoints" are VFR reporting points, not IFR fixes
    const m = new Map<string, DraftPoint>();
    for (const p of navPoints) {
      const type = typeForCupPoint(p);
      if (type !== "Airport" && type !== "VOR" && type !== "NDB" && type !== "IFR Fix" && !p.isIfrFix) continue;
      const [lat, lon] = getLatLngTupleForCupPoint(p);
      if (p.code && !m.has(p.code)) m.set(p.code.toUpperCase(), { ident: p.code.toUpperCase(), lat, lon });
    }
    return m;
  }, [navPoints]);

  const vfrByCode = useMemo(() => {
    //VFR reporting points — recognized for mixed-rules (Y/Z) route strings and the draft line, but
    //deliberately kept OUT of the IFR resolver so they never auto-classify in pure IFR routes.
    //Only single-token designators qualify: CUP codes with spaces/slashes ("A/F FLABOURO",
    //"ABEAM AGIOS") cannot appear in a Field-15 string.
    const m = new Map<string, DraftPoint>();
    for (const p of navPoints) {
      const type = typeForCupPoint(p);
      if (type === "Airport" || type === "VOR" || type === "NDB" || type === "IFR Fix") continue;
      const code = (p.code ?? "").toUpperCase().trim().replace(/\s+/g, " ");
      //Single-word designators, plus multi-word names keyed by their sentinel-joined form so they
      //resolve as one point (see PHRASE_JOIN).
      const single = /^[A-Z0-9]{2,10}$/.test(code);
      const phrase = !single && /^[A-Z0-9]+(?: [A-Z0-9]+)+$/.test(code) && code.length <= 30;
      if (!single && !phrase) continue;
      const key = single ? code : code.split(" ").join(PHRASE_JOIN);
      const [lat, lon] = getLatLngTupleForCupPoint(p);
      if (!m.has(key)) m.set(key, { ident: key, lat, lon });
    }
    return m;
  }, [navPoints]);

  //The multi-word VFR point names, longest first so "ABEAM AGIOS NIKOLAOS" wins over "ABEAM AGIOS".
  const vfrPhrases = useMemo(
    () =>
      Array.from(vfrByCode.keys())
        .filter((k) => k.includes(PHRASE_JOIN))
        .map(phraseToDisplay)
        .sort((a, b) => b.length - a.length),
    [vfrByCode]
  );

  //The full manual route as one Field-15 string, whichever editor is active.
  //Leaves the planner with real spaces: a multi-word point files as "EAST AIGINA", same as VFR.
  const effectiveManualRoute = phraseToDisplay(
    (textMode ? textDraft : manualRoute + " " + manualTail).toUpperCase().replace(/\s+/g, " ").trim()
  );

  const draftRoute = useMemo(
    () =>
      effectiveManualRoute.length > 0
        ? computeDraftRoute(effectiveManualRoute, (id) => cupByCode.get(id) ?? vfrByCode.get(id), flightRules, vfrPhrases)
        : [],
    [effectiveManualRoute, cupByCode, vfrByCode, flightRules, vfrPhrases]
  );

  //Rules-flagged geometry for a stored route string (checked/armed options) — same per-leg VFR/IFR
  //classification the draft uses, so a mixed route keeps its colours once checked or armed.
  const field15Legs = (field15: string): DraftPoint[] =>
    computeDraftRoute(field15, (id) => cupByCode.get(id) ?? vfrByCode.get(id), flightRules, vfrPhrases);

  const cupAirportCodes = useMemo(
    () =>
      navPoints
        .filter((p) => typeForCupPoint(p) === "Airport" && /^[A-Z]{4}$/.test((p.code ?? "").toUpperCase()))
        .map((p) => p.code.toUpperCase()),
    [navPoints]
  );

  const vfrPointCodes = useMemo(() => Array.from(vfrByCode.keys()), [vfrByCode]);

  const navaidCodes = useMemo(
    () =>
      navPoints
        .filter((p) => {
          const t = typeForCupPoint(p);
          return (t === "VOR" || t === "NDB") && /^[A-Z]{2,5}$/.test((p.code ?? "").toUpperCase().trim());
        })
        .map((p) => p.code.toUpperCase().trim()),
    [navPoints]
  );

  //Which rules apply at the caret? Mixed (Y/Z) routes toggle at each IFR/VFR marker — inside a VFR
  //segment the autocomplete and the map switch to the visual world (reporting points, no airways).
  const caretInVfrSegment = (() => {
    if (mode !== "manual" || (flightRules !== "Y" && flightRules !== "Z")) return false;
    let vfrNow = flightRules === "Z"; //Z starts VFR, Y starts IFR
    const source = textMode ? textDraft : manualRoute;
    for (const t of source.toUpperCase().trim().split(/\s+/)) {
      if (t === "IFR") vfrNow = false;
      else if (t === "VFR") vfrNow = true;
    }
    return vfrNow;
  })();

  //Default the layers to the section being edited — VFR points in a VFR segment, IFR points (airways)
  //in an IFR segment / during pick-up. Runs only on section changes, so a manual toggle in between
  //persists until the next transition.
  useEffect(() => {
    if (pickupDraft) {
      //pick-up editor = IFR mode
      setVfrLayerVisible(false);
      setAirwaysLayerVisible(true);
      return;
    }
    if (caretInVfrSegment) {
      setVfrLayerVisible(true);
      if (isMixedRules) setAirwaysLayerVisible(false);
    } else {
      setAirwaysLayerVisible(true);
      if (isMixedRules) setVfrLayerVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caretInVfrSegment, !!pickupDraft, isMixedRules]);

  const routeSuggestions = useMemo(
    () =>
      checkManualRouteAction
        ? computeRouteSuggestions(
            manualRoute,
            cupAirportCodes,
            vfrPointCodes,
            caretInVfrSegment,
            (id) => cupByCode.get(id) ?? vfrByCode.get(id),
            planDep.trim(),
            navaidCodes
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manualRoute, cupAirportCodes, vfrPointCodes, caretInVfrSegment, cupByCode, vfrByCode, planDep, navaidCodes]
  );

  //One token, and a token actually on offer below — a fabricated "ROPOX M749 SKP …" both invents
  //fixes that may not apply to this pairing and reads as an already-typed route. Tab accepts this same
  //first suggestion, so the hint and the shortcut now agree.
  const routePlaceholder = routeSuggestions.length > 0 ? `${phraseToDisplay(routeSuggestions[0].ident)} …` : "";

  const acceptSuggestion = (sug: RouteSuggestion) => {
    setManualRoute((prev) => {
      const endsWithSpace = /\s$/.test(prev) || prev.length === 0;
      const base = endsWithSpace ? prev : prev.replace(/[^\s]+$/, "");
      const lastFix = lastConnectableFix(base);
      let insert = sug.ident;
      if (sug.kind === "fix" && lastFix && FIX_BY_IDENT.has(sug.ident)) {
        insert = networkTokensBetween(lastFix, sug.ident) ?? insert;
      }
      return base + insert + " ";
    });
  };

  //DEP / DEST / ALTN aerodromes from the (editable) planning inputs, resolved via the nav database.
  const aerodromeMarkers = useMemo(() => {
    const defs: { role: string; code: string; color: string }[] = [
      { role: "DEP", code: planDep, color: "#35d27a" },
      { role: "DEST", code: planDest, color: "#ff5964" },
      { role: "ALTN", code: planAltn1, color: "#ffb02e" },
      { role: "ALTN2", code: planAltn2, color: "#ffb02e" },
    ];
    return defs
      .filter((d) => /^[A-Z]{4}$/.test(d.code.trim()))
      .map((d) => ({ ...d, code: d.code.trim(), point: cupByCode.get(d.code.trim()) }))
      .filter((d): d is typeof d & { point: DraftPoint } => d.point !== undefined);
  }, [planDep, planDest, planAltn1, planAltn2, cupByCode]);

  if (isIfr === false) return null;

  const pendingCount = (results ?? []).filter((r) => r.status === "pending").length;
  const failedCount = (results ?? []).filter((r) => r.status === "failed").length;

  const selectedOptionKey = options.find((o) => o.result.field15 === selectedField15)?.key;

  //Each mode lists only its own results: Find shows autorouter results, Build manually shows
  //checked routes. The flight's current route and the armed route stay visible in both.
  const visibleOptions = options.filter((o) => {
    if (o.result.field15 === selectedField15) return true;
    if (o.optimizes.includes("current")) return true;
    const manualOnly = o.optimizes.every((m) => m === "manual");
    return mode === "manual" ? o.optimizes.includes("manual") : !manualOnly;
  });

  const visibleKeyOrUndefined = (key?: string) => (key && visibleOptions.some((o) => o.key === key) ? key : undefined);
  const focusedKey = visibleKeyOrUndefined(hoverKey) ?? visibleKeyOrUndefined(pinnedKey) ?? selectedOptionKey ?? visibleOptions[0]?.key;
  const focusedOption = options.find((o) => o.key === focusedKey);

  const colorForOption = (option: RouteOption, index: number): string =>
    option.result.field15 === selectedField15 ? ARMED_COLOR : ROUTE_COLORS[index % ROUTE_COLORS.length];

  // DEP → DEST readout, derived from the first computed option.
  const firstWpts = options[0]?.result.waypoints ?? [];
  const depIdent = planDep.trim() || (firstWpts[0]?.ident ?? firstWpts[0]?.name);
  const destIdent = planDest.trim() || (firstWpts.at(-1)?.ident ?? firstWpts.at(-1)?.name);

  const levelChoices = Array.from(new Set([...(baseLevel && baseLevel > 0 ? [baseLevel] : []), ...LEVEL_CHOICES, ...customLevels])).sort((a, b) => a - b);
  const effectiveLevelCount = Math.max(selectedLevels.length, 1);
  const comboCount = optimizes.length * effectiveLevelCount;

  const toggleOptimize = (key: string) => {
    setOptimizes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleLevel = (level: number) => {
    setSelectedLevels((prev) => (prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level].sort((a, b) => a - b)));
  };

  //Commit the "+ FL" input: the level joins the chip row and is selected right away (unless that
  //would exceed the run budget — then it's just added as a chip to select manually).
  const addCustomLevel = () => {
    const lvl = Number(customLevelText);
    setCustomLevelText("");
    if (!Number.isFinite(lvl) || lvl < 10 || lvl > 660) return;
    setCustomLevels((prev) => (prev.includes(lvl) ? prev : [...prev, lvl]));
    const wouldExceed = optimizes.length * (selectedLevels.length + 1) > MAX_COMBOS;
    if (!selectedLevels.includes(lvl) && !wouldExceed) toggleLevel(lvl);
  };

  //Fire one request per optimize profile so results stream in as each run completes,
  //instead of waiting for the slowest one.
  const handleFind = () => {
    if (!findIfrRoutesAction || optimizes.length === 0) return;
    setError(undefined);
    setValidation(undefined);
    setResults(currentRouteOption ? [currentRouteOption] : []);
    setPinnedKey(undefined);
    setHoverKey(undefined);
    setExpandedKey(undefined);
    setFinding(true);
    const levels = selectedLevels.length > 0 ? selectedLevels : undefined;
    const overrides = planningOverrides();
    const signature = planningSignature;
    let remaining = optimizes.length;
    optimizes.forEach((mode) => {
      findIfrRoutesAction([mode], levels, overrides)
        .then((res) => {
          setResults((prev) => [...(prev ?? []), ...res]);
          recordOptionSignatures(res, signature);
        })
        .catch((e: any) => setError(typeof e === "string" ? e : e?.message ?? "Route find failed."))
        .finally(() => {
          remaining -= 1;
          if (remaining === 0) setFinding(false);
        });
    });
  };

  //Prefer the upper-airway ident (UL617) at/above FL200, else the lower one (L617).
  const airwayIdentForLevel = (awy: AirwayDef): string => {
    const lvl = selectedLevels[0] ?? baseLevel ?? 0;
    const upper = awy.idents.find((i) => i.startsWith("U"));
    const lower = awy.idents.find((i) => !i.startsWith("U")) ?? awy.idents[0];
    return lvl >= 200 && upper ? upper : lower;
  };

  //Field-15 tokens routing `from` -> `to` across the airway network ("M749 SKP L617 TSL M603 PEREN"),
  //or undefined when the fixes aren't connected.
  const networkTokensBetween = (fromIdent: string, toIdent: string): string | undefined => {
    const segments = findNetworkPath(fromIdent, toIdent);
    if (!segments) return undefined;
    return segments.map((seg) => `${airwayIdentForLevel(seg.awy)} ${seg.exit}`).join(" ");
  };

  //Insertions land at the caret: end of the head string in pill mode, end of the text in text mode.
  const appendRouteText = (updater: (current: string) => string) => {
    if (textMode) setTextDraft(updater);
    else setManualRoute(updater);
  };

  //Appending a fix directly after another fix auto-inserts the airway that connects them
  //("ROPOX" + click SKP -> "ROPOX M749 SKP") — bare fix-to-fix would read as DCT, which Greek
  //airspace rejects. No shared airway -> plain append (the token row will show what's missing).
  const appendToManualRoute = (code: string) => {
    setMode("manual");
    appendRouteText((prev) => {
      const t = prev.trim();
      const lastFix = lastConnectableFix(t);
      //a multi-word name goes in sentinel-joined so the editor treats it as one token
      let insert = code.toUpperCase().trim().replace(/\s+/g, " ").split(" ").join(PHRASE_JOIN);
      if (lastFix && FIX_BY_IDENT.has(insert)) {
        insert = networkTokensBetween(lastFix, insert) ?? insert;
      }
      return (t.length > 0 ? t + " " : "") + insert + " ";
    });
  };

  //Alternative airway idents that can serve the same leg (same neighbouring fixes) as the airway
  //token at `idx` — parallel airways plus the lower/upper variant of the current one.
  const airwayAlternatives = (tokens: string[], idx: number): string[] => {
    const current = tokens[idx];
    const prevTok = tokens[idx - 1];
    const nextTok = tokens[idx + 1];
    const out = new Set<string>();
    if (prevTok && nextTok && FIX_BY_IDENT.has(prevTok) && FIX_BY_IDENT.has(nextTok)) {
      for (const awy of AIRWAYS) {
        if (airwayPathBetween(awy, prevTok, nextTok)) awy.idents.forEach((id) => out.add(id));
      }
    } else {
      (AIRWAYS_BY_IDENT.get(current) ?? []).forEach((awy) => awy.idents.forEach((id) => out.add(id)));
    }
    out.delete(current);
    return Array.from(out).slice(0, 8);
  };

  //Clicking an airway segment appends "FROM AWY TO" — or just "AWY TO" when the route already ends at FROM,
  //so consecutive segment clicks chain naturally (ATV L995 KEA L617 AKORO ...).
  const appendAirwaySegment = (seg: AirwaySegment) => {
    setMode("manual");
    const awy = seg.airway.idents[0];
    appendRouteText((prev) => {
      const t = prev.trim();
      const lastFix = lastConnectableFix(t);
      if (lastFix === seg.from.ident) return `${t} ${awy} ${seg.to.ident} `;
      if (lastFix === seg.to.ident) return `${t} ${awy} ${seg.from.ident} `;
      return (t.length > 0 ? t + " " : "") + `${seg.from.ident} ${awy} ${seg.to.ident} `;
    });
  };

  //Nav-point marker with a popup; "Add to route" appends the ident to the manual route box.
  //Only Field-15-valid designators are appendable: 4-letter ICAO for airports (from the code or a
  //"[LGSY]" suffix in the name), 2-5 letter codes for navaids/points — never plain-language names.
  const appendableCode = (p: CupPoint, type: string): string | undefined => {
    const code = (p.code ?? "").toUpperCase().trim();
    if (type === "Airport") {
      if (/^[A-Z]{4}$/.test(code)) return code;
      const m = (p.name ?? "").match(/\[([A-Z]{4})\]/);
      return m ? m[1] : undefined;
    }
    if (type === "VOR" || type === "NDB" || type === "IFR Fix") return /^[A-Z0-9]{2,5}$/.test(code) ? code : undefined;
    //VFR reporting points (mixed Y/Z routes): longer designators like ROUPOL are fine in VFR segments,
    //and so are multi-word names like "EAST AIGINA" — VFR routes file those verbatim, so we do too.
    const collapsed = code.replace(/\s+/g, " ");
    if (/^[A-Z0-9]{2,10}$/.test(collapsed)) return collapsed;
    return /^[A-Z0-9]+(?: [A-Z0-9]+)+$/.test(collapsed) && collapsed.length <= 30 ? collapsed : undefined;
  };

  const renderNavMarker = (p: CupPoint, i: number) => {
    const type = typeForCupPoint(p);
    const filableCode = appendableCode(p, type);
    const addCode = checkManualRouteAction ? filableCode : undefined;
    return (
      <Marker
        key={`${p.code}-${i}`}
        position={getLatLngTupleForCupPoint(p)}
        opacity={0.85}
        icon={Leaflet.icon({ iconSize: [22, 22], iconUrl: iconUrlForCupPoint(p) })}
        title={p.code}
      >
        <Popup>
          <div className="fw-semibold">{p.code}</div>
          <div className="small">
            {type}
            {p.name && p.name.toUpperCase() !== p.code ? ` · ${p.name}` : ""}
            {p.freq ? ` · ${p.freq}` : ""}
            {type === "IFR Fix" && p.desc ? ` · ${p.desc}` : ""}
          </div>
          {addCode && (
            <div className="d-grid mt-2">
              <Button size="sm" variant="dark" onClick={() => { appendToManualRoute(addCode); mapRef.current?.closePopup(); }}>
                Add {addCode} to route
              </Button>
            </div>
          )}
          {/* Say WHY there is no button: a name like "EAST AIGINA" is not a Field-15 designator, and
              since Field 15 is space-delimited it would tokenise into two unresolvable words. */}
          {checkManualRouteAction && !filableCode && (
            <div className="small text-muted mt-2">
              Cannot be added to Field 15 — this point has no single-word designator.
            </div>
          )}
        </Popup>
      </Marker>
    );
  };

  //Remove a checked route from the list: drops only its "manual" results, so an identical route
  //that was ALSO found by the autorouter stays in the Found list.
  const removeCheckedOption = (option: RouteOption) => {
    setResults((prev) =>
      (prev ?? []).filter((r) => r.status !== "found" || r.optimize !== "manual" || `${r.field15}|${r.level ?? ""}` !== option.key)
    );
    if (pinnedKey === option.key) setPinnedKey(undefined);
    if (expandedKey === option.key) setExpandedKey(undefined);
    setHoverKey(undefined);
  };

  //Check a hand-written route: it joins the option list (and map) like any found route.
  const handleCheckManual = async () => {
    if (!checkManualRouteAction || effectiveManualRoute.length === 0) return;
    setError(undefined);
    setCheckingManual(true);
    try {
      const result = await checkManualRouteAction(effectiveManualRoute, planningOverrides());
      setResults((prev) => [...(prev ?? []), result]);
      recordOptionSignatures([result], planningSignature);
      if (result.status === "found") {
        //pin + auto-expand the fresh check so its validation/ATFM details are immediately visible
        const key = `${result.field15}|${result.level ?? ""}`;
        setPinnedKey(key);
        setExpandedKey(key);
        setManualRoute("");
        setManualTail("");
        setTextDraft("");
        setOpenAirwayToken(undefined);
        setBuilderExpanded(false); //route is now in the list — collapse the empty builder again
      } else if (result.status === "failed") {
        setError(result.error);
      }
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message ?? "Route check failed.");
    } finally {
      setCheckingManual(false);
    }
  };

  const handleSelect = async (field15: string) => {
    if (!selectIfrRouteAction) return;
    setError(undefined);
    setSelectingField15(field15);
    try {
      const chosen = options.find((o) => o.result.field15 === field15)?.result;
      const chosenLevel = chosen?.level;
      //carry the route distance (aircraft-independent) so the flight's EET can be computed on the fly
      //from it and the aircraft's cruise speed, like a VFR route
      const distanceNM = chosen?.routeDist !== undefined && chosen.routeDist > 0 ? Math.round(chosen.routeDist) : undefined;
      const result = await selectIfrRouteAction(field15, {
        ...planningOverrides(),
        level: chosenLevel ?? (Number(planFL) > 0 ? Number(planFL) : undefined),
        distanceNM,
      });
      onRouteSelected(result);
      //Hand the whole armed option up: its geometry and IFPUV result are already paid for.
      if (chosen && chosen.status === "found") onRouteArmed?.(chosen);
      setSelectedField15(field15);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message ?? "Route select failed.");
    } finally {
      setSelectingField15(undefined);
    }
  };

  //Disarm: clear the selected route (form: cleared on save; FPL page: cleared immediately).
  const handleDisarm = async () => {
    if (!selectIfrRouteAction) return;
    setError(undefined);
    const wasArmed = selectedField15;
    setSelectingField15("__disarm__");
    try {
      const result = await selectIfrRouteAction("");
      onRouteSelected(result);
      setSelectedField15(undefined);
      //Load the just-disarmed route into the manual builder so "disarm to change" lets the user edit
      //it directly rather than rebuilding from scratch.
      if (wasArmed && checkManualRouteAction) {
        setMode("manual");
        setSetupDone(true);
        setTextMode(false);
        setManualTail("");
        setTextDraft("");
        //re-join multi-word VFR names so each comes back as ONE pill
        setManualRoute(joinKnownPhrases(wasArmed.trim().toUpperCase(), vfrPhrases) + " ");
      }
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message ?? "Disarm failed.");
    } finally {
      setSelectingField15(undefined);
    }
  };

  //Closing the planner: if it opened with a route that has since been disarmed, the route would be
  //lost on close — offer to revert to it or discard. Otherwise close normally.
  const requestClose = () => {
    if (initialArmedRoute.current && !selectedField15) {
      setShowCloseConfirm(true);
    } else {
      onHide();
    }
  };

  const revertAndClose = async () => {
    setShowCloseConfirm(false);
    if (initialArmedRoute.current && selectIfrRouteAction) {
      try {
        const r = await selectIfrRouteAction(initialArmedRoute.current);
        onRouteSelected(r);
      } catch { /* revert is best-effort; close either way */ }
    }
    onHide();
  };

  const handleValidate = async () => {
    if (!validateFplAction) return;
    setError(undefined);
    setValidating(true);
    try {
      const resp = await validateFplAction();
      setValidation(resp.validation);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message ?? "Validation failed.");
    } finally {
      setValidating(false);
    }
  };

  // ATFM overlays: focused route's regulation areas (all visible routes' areas deduped when nothing focused).
  const delayOverlays = (focusedOption ? [focusedOption] : visibleOptions)
    .flatMap((o) => o.result.delays ?? [])
    .filter((d) => d.geometry)
    .filter((d, i, arr) => arr.findIndex((x) => x.locationIdent === d.locationIdent && x.startTime === d.startTime) === i);

  //---- manual editor token views (head = before the caret, tail = after) ----
  const headEndsWithSpace = /\s$/.test(manualRoute) || manualRoute.length === 0;
  const headAllTokens = manualRoute.trim().length > 0 ? manualRoute.trim().split(/\s+/) : [];
  const manualCommitted = headEndsWithSpace ? headAllTokens : headAllTokens.slice(0, -1);
  const manualPartial = headEndsWithSpace ? "" : headAllTokens.at(-1) ?? "";
  const manualCommittedPrefix = manualCommitted.length > 0 ? manualCommitted.join(" ") + " " : "";
  const manualTailTokens = manualTail.trim().length > 0 ? manualTail.trim().split(/\s+/) : [];

  const sideTokens = (side: "head" | "tail") => (side === "head" ? manualCommitted : manualTailTokens);

  //Rebuild both sides around a new caret position and refocus the input there.
  const setCaretTokens = (head: string[], tail: string[]) => {
    setManualRoute(head.join(" ") + (head.length > 0 ? " " : ""));
    setManualTail(tail.join(" "));
    setOpenAirwayToken(undefined);
    setTimeout(() => manualInputRef.current?.focus(), 0);
  };

  //Clicking a pill moves the caret right after it (a half-typed token commits in place).
  const placeCaretAfterToken = (side: "head" | "tail", i: number) => {
    if (side === "head") {
      setCaretTokens(manualCommitted.slice(0, i + 1), [
        ...manualCommitted.slice(i + 1),
        ...(manualPartial ? [manualPartial] : []),
        ...manualTailTokens,
      ]);
    } else {
      setCaretTokens(
        [...manualCommitted, ...(manualPartial ? [manualPartial] : []), ...manualTailTokens.slice(0, i + 1)],
        manualTailTokens.slice(i + 1)
      );
    }
  };

  //Place the caret at an absolute token position (0..N) across the whole route — used when the user
  //clicks in the empty space between pills, so the caret lands at the nearest gap.
  const placeCaretAtGlobal = (pos: number) => {
    const all = [...manualCommitted, ...(manualPartial ? [manualPartial] : []), ...manualTailTokens];
    const p = Math.max(0, Math.min(pos, all.length));
    setCaretTokens(all.slice(0, p), all.slice(p));
  };

  //Clicking anywhere in the editor's empty space: find the nearest token boundary to the pointer
  //(same row first) and drop the caret there. Clicks on a pill / × / input keep their own behaviour.
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest(".ifr-token, .ifr-awy-pill, .ifr-token-x, .ifr-gap-pill, input")) return;
    const wraps = Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-after]"));
    if (wraps.length === 0) {
      manualInputRef.current?.focus();
      return;
    }
    const x = e.clientX;
    const y = e.clientY;
    let best: HTMLElement | undefined;
    let bestScore = Infinity;
    let after = true;
    for (const w of wraps) {
      const r = w.getBoundingClientRect();
      const cy = (r.top + r.bottom) / 2;
      const cx = (r.left + r.right) / 2;
      //same-row clicks dominate (row distance weighted heavily), then nearest by x
      const score = Math.abs(cy - y) * 1000 + Math.abs(cx - x);
      if (score < bestScore) {
        bestScore = score;
        best = w;
        after = x >= cx;
      }
    }
    if (best) {
      const globalAfter = Number(best.dataset.after);
      placeCaretAtGlobal(after ? globalAfter : globalAfter - 1);
    } else {
      manualInputRef.current?.focus();
    }
  };

  const removeManualToken = (side: "head" | "tail", i: number) => {
    if (side === "head") {
      const rest = manualCommitted.filter((_, j) => j !== i);
      setManualRoute(rest.join(" ") + (rest.length > 0 || manualPartial ? " " : "") + manualPartial);
    } else {
      setManualTail(manualTailTokens.filter((_, j) => j !== i).join(" "));
    }
    setOpenAirwayToken(undefined);
  };

  const insertManualTokensAt = (side: "head" | "tail", idx: number, insert: string[]) => {
    if (side === "head") {
      const tokens = [...manualCommitted];
      tokens.splice(idx, 0, ...insert);
      setManualRoute(tokens.join(" ") + " " + manualPartial);
    } else {
      const tokens = [...manualTailTokens];
      tokens.splice(idx, 0, ...insert);
      setManualTail(tokens.join(" "));
    }
    setOpenAirwayToken(undefined);
  };

  const swapManualToken = (side: "head" | "tail", idx: number, newIdent: string) => {
    if (side === "head") {
      setManualRoute(manualCommitted.map((t, j) => (j === idx ? newIdent : t)).join(" ") + " " + manualPartial);
    } else {
      setManualTail(manualTailTokens.map((t, j) => (j === idx ? newIdent : t)).join(" "));
    }
    setOpenAirwayToken(undefined);
  };

  const clearManualRoute = () => {
    setManualRoute("");
    setManualTail("");
    setTextDraft("");
    setOpenAirwayToken(undefined);
  };

  //---- mixed-rules (Y/Z) helpers: compose the rules-change tokens without hand-typing the syntax ----

  //Open the IFR pick-up editor. The pick-up must be an IFR fix (airway-network entry) — a VFR
  //reporting point cannot join the airway system, so the last token only prefills when it IS a fix.
  const openIfrPickup = () => {
    const last = manualCommitted[manualCommitted.length - 1] ?? "";
    setPickupDraft({
      point: FIX_BY_IDENT.has(last) ? last : "",
      speed: cruiseSpeedKt && cruiseSpeedKt > 0 ? String(Math.round(cruiseSpeedKt)) : "",
      level: Number(planFL) > 0 ? planFL : "",
    });
  };

  //Tapping a ▲ fix on the map: while the pick-up editor is open it SETS the pick-up point
  //(the map is in IFR mode then); otherwise it appends to the route as usual.
  const handleFixTap = (ident: string) => {
    if (pickupDraft) {
      setPickupDraft({ ...pickupDraft, point: ident });
      return;
    }
    appendToManualRoute(ident);
  };

  //Nearest IFR fixes to the end of the current route — offered as pick-up candidates.
  const pickupFixSuggestions = (() => {
    if (!pickupDraft) return [] as string[];
    const q = pickupDraft.point;
    let refPos: { lat: number; lon: number } | undefined;
    for (let i = manualCommitted.length - 1; i >= 0 && !refPos; i--) {
      const m = manualCommitted[i].match(POINT_CHANGE_RE);
      const id = m ? m[1] : manualCommitted[i];
      refPos = FIX_BY_IDENT.get(id) ?? cupByCode.get(id) ?? vfrByCode.get(id);
    }
    if (!refPos) refPos = cupByCode.get(planDep.trim());
    const cands = AIRWAY_FIXES.filter((f) => f.ident.startsWith(q) && f.ident !== q);
    if (refPos) {
      const r = refPos;
      const d2 = (f: AirwayFix) => (f.lat - r.lat) ** 2 + ((f.lon - r.lon) * Math.cos((r.lat * Math.PI) / 180)) ** 2;
      cands.sort((a, b) => d2(a) - d2(b));
    }
    return cands.slice(0, 6).map((f) => f.ident);
  })();

  const pickupPreview = pickupDraft
    ? `${pickupDraft.point || "?"}/N${(pickupDraft.speed || "0").padStart(4, "0")}F${(pickupDraft.level || "0").padStart(3, "0")} IFR`
    : "";

  //Insert the pick-up group: replaces the point before the caret when it matches, else appends.
  const confirmIfrPickup = () => {
    if (!pickupDraft) return;
    const point = pickupDraft.point.toUpperCase().trim();
    const spd = Number(pickupDraft.speed);
    const lvl = Number(pickupDraft.level);
    if (!(spd > 0) || !(lvl > 0)) {
      setError("The IFR pick-up needs a cruise speed (kt) and a flight level.");
      return;
    }
    if (!FIX_BY_IDENT.has(point)) {
      setError("The IFR pick-up must be an IFR fix on the airway network (e.g. SOSUS) — pick one of the suggested fixes.");
      return;
    }
    setError(undefined);
    const group = `${point}/N${String(Math.round(spd)).padStart(4, "0")}F${String(Math.round(lvl)).padStart(3, "0")} IFR`;
    const tokens = [...manualCommitted];
    if (tokens.length > 0 && tokens[tokens.length - 1] === point) {
      tokens[tokens.length - 1] = group;
    } else {
      tokens.push(group);
    }
    setManualRoute(tokens.join(" ") + " " + manualPartial);
    setPickupDraft(undefined);
  };

  //Cancel IFR at the point before the caret: "... AMALA" -> "... AMALA VFR".
  const insertVfrCancel = () => {
    if (manualCommitted.length === 0) return;
    setError(undefined);
    setManualRoute(manualCommitted.join(" ") + " VFR " + manualPartial);
  };


  //Switch pill editor <-> free text; leaving text mode re-tokenizes the string into pills.
  const toggleTextMode = () => {
    if (textMode) {
      //re-join multi-word VFR names so each becomes a single pill again
      const asPills = joinKnownPhrases(effectiveManualRoute, vfrPhrases);
      setManualRoute(asPills.length > 0 ? asPills + " " : "");
      setManualTail("");
      setTextMode(false);
      setTimeout(() => manualInputRef.current?.focus(), 0);
    } else {
      setTextDraft(effectiveManualRoute);
      setOpenAirwayToken(undefined);
      setTextMode(true);
    }
  };

  return (
    <Modal show={show} onHide={requestClose} fullscreen contentClassName="ifr-console" dialogClassName="ifr-console-modal">
      <Modal.Header closeButton>
        <div className="ifr-console-title">
          IFR Route Planner
          {depIdent && destIdent && (
            <span className="ifr-title-route">
              {depIdent} → {destIdent}
            </span>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="ifr-console-body">
        {/* ------------- left rail ------------- */}
        <div className="ifr-rail">
          <div className={"ifr-rail-scroll" + (selectedField15 ? " armed-mode" : "")}>
            {/* When a route is armed, this card is the only thing shown — the building tools are
                hidden so the armed route reads clearly. Disarm to change it. */}
            {selectedField15 && (
              <div className="ifr-armed-block">
                <div className="ifr-armed-head">
                  <span className="ifr-annunciator armed">
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: "0.65rem" }} /> Route armed
                  </span>
                  <span className="ifr-status-line">
                    {persistedOnSelect ? "Saved to the plan." : "Saved when you save the flight."}
                  </span>
                </div>
                <div className="ifr-armed-route mt-2">{selectedField15}</div>
                <button
                  className="ifr-ghost-btn w-100 mt-3"
                  onClick={handleDisarm}
                  disabled={selectingField15 !== undefined}
                >
                  {selectingField15 === "__disarm__" ? "Disarming…" : "Disarm to change the route"}
                </button>
              </div>
            )}

            <div className="ifr-section">
              <div className="ifr-section-title">{entityKind === "route" ? "Route" : "Flight"}</div>
              <div className="d-flex gap-2 mb-2">
                {/* No sample-ICAO placeholders here: greyed "LGMG"/"LGKM" in an empty box read as values
                    that are already set, so an unfilled form looks complete and the disabled Continue
                    button looks broken. */}
                {([
                  ["Departure", planDep, setPlanDep],
                  ["Destination", planDest, setPlanDest],
                ] as [string, string, (v: string) => void][]).map(([label, value, setter]) => (
                  <div key={label} style={{ flex: 1, minWidth: 0 }}>
                    <div className="ifr-input-label">{label}</div>
                    <input className="ifr-plan-input" value={value} maxLength={4} spellCheck={false} onChange={(e) => setter(e.target.value.toUpperCase())} />
                  </div>
                ))}
              </div>
              <div className="d-flex gap-2 mb-2">
                {([
                  ["Alternate 1", planAltn1, setPlanAltn1],
                  ["Alternate 2", planAltn2, setPlanAltn2],
                ] as [string, string, (v: string) => void][]).map(([label, value, setter]) => (
                  <div key={label} style={{ flex: 1, minWidth: 0 }}>
                    <div className="ifr-input-label">{label}</div>
                    <input className="ifr-plan-input" value={value} maxLength={4} spellCheck={false} onChange={(e) => setter(e.target.value.toUpperCase())} />
                  </div>
                ))}
              </div>
              <div className="ifr-input-label">
                {entityKind === "route" ? "Validate against date & time" : "Off-block date & time"}
              </div>
              <input
                type="datetime-local"
                className="ifr-plan-input"
                style={{ textAlign: "left", textTransform: "none" }}
                value={planDate}
                onChange={(e) => setPlanDate(e.target.value)}
              />
              <div className="ifr-status-line mt-1">
                {entityKind === "route"
                  ? "Alternates are saved with the route. The date & time is only what the route is validated against — it is not saved."
                  : "Applied to the flight when you select a route."}
              </div>
              {!setupDone && (
                <button
                  className="ifr-find-btn mt-3"
                  disabled={!(/^[A-Z]{4}$/.test(planDep.trim()) && /^[A-Z]{4}$/.test(planDest.trim()))}
                  onClick={() => setSetupDone(true)}
                >
                  Continue to route planning
                </button>
              )}
            </div>

            {setupDone && (
            <>
            <div className="ifr-section">
              <div className="ifr-section-title">
                Route
                {isMixedRules && (
                  <>
                    <RulesTypeBadge flightRules={flightRules} style={{ marginLeft: "0.5rem" }} />
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <BsTooltip id="ifr-mixed-help" className="ifr-help-tooltip">
                          {flightRules === "Z" ? (
                            <>
                              <b>Z — start VFR.</b> Add your VFR points, then <b>+ IFR pick-up</b> at the fix where IFR
                              begins; continue on airways.
                            </>
                          ) : (
                            <>
                              <b>Y — start IFR.</b> Build the IFR airway route, then <b>+ cancel IFR</b> at the fix where
                              you go visual; add the VFR points.
                            </>
                          )}
                        </BsTooltip>
                      }
                    >
                      <button type="button" className="ifr-info-btn" aria-label="How to build a mixed route">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </button>
                    </OverlayTrigger>
                  </>
                )}
              </div>
              {isMixedRules ? null : (
                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className={"ifr-profile-btn flex-fill" + (mode === "find" ? " active" : "")}
                    onClick={() => setMode("find")}
                  >
                    Find routes
                  </button>
                  {checkManualRouteAction && (
                    <button
                      type="button"
                      className={"ifr-profile-btn flex-fill" + (mode === "manual" ? " active" : "")}
                      onClick={() => setMode("manual")}
                    >
                      Build manually
                    </button>
                  )}
                </div>
              )}

            {mode === "find" && (
              <>
                <div className="ifr-label">Optimize profile</div>
            <div className="d-flex gap-2 mb-3">
              {ALL_OPTIMIZES.map((key) => {
                const active = optimizes.includes(key);
                const wouldExceed = !active && (optimizes.length + 1) * effectiveLevelCount > MAX_COMBOS;
                return (
                  <button
                    key={key}
                    type="button"
                    className={"ifr-profile-btn" + (active ? " active" : "")}
                    disabled={wouldExceed}
                    onClick={() => toggleOptimize(key)}
                  >
                    {optimizeLabel(key)}
                  </button>
                );
              })}
            </div>

            <div className="ifr-label">Explore flight levels</div>
            <div className="d-flex flex-wrap gap-2 mb-1">
              {levelChoices.map((level) => {
                const active = selectedLevels.includes(level);
                const wouldExceed = !active && optimizes.length * (selectedLevels.length + 1) > MAX_COMBOS;
                return (
                  <button
                    key={level}
                    type="button"
                    className={"ifr-profile-btn" + (active ? " active" : "")}
                    disabled={wouldExceed}
                    onClick={() => toggleLevel(level)}
                  >
                    {flLabel(level)}
                  </button>
                );
              })}
              <input
                className="ifr-plan-input ifr-level-input"
                value={customLevelText}
                placeholder="+ FL"
                maxLength={3}
                inputMode="numeric"
                title="Type a flight level (e.g. 110) and press Enter"
                onChange={(e) => setCustomLevelText(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomLevel();
                  }
                }}
                onBlur={addCustomLevel}
              />
            </div>
            <div className="ifr-status-line mb-3">
              {selectedLevels.length === 0 ? "Using the plan's flight level. " : ""}
              {comboCount} run{comboCount !== 1 ? "s" : ""} · max {MAX_COMBOS}
            </div>

            <button className="ifr-find-btn mb-2" onClick={handleFind} disabled={finding || !findIfrRoutesAction || optimizes.length === 0}>
              {finding ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Scanning…
                </>
              ) : results ? (
                "Re-find routes"
              ) : (
                "Find routes"
              )}
            </button>

            {finding && <div className="ifr-scan-hint mb-2">Live EUROCONTROL routing + IFPS validation</div>}
              </>
            )}

            {/* collapse the empty builder when a route is already checked — it only clutters the
                routes-selection view; a button reopens it to build/edit another */}
            {mode === "manual" && checkManualRouteAction && effectiveManualRoute.length === 0 && !builderExpanded && (results ?? []).some((r) => r.status === "found") && (
              <button className="ifr-ghost-btn w-100 mt-2" onClick={() => setBuilderExpanded(true)}>
                + Build or edit a route
              </button>
            )}

            {mode === "manual" && checkManualRouteAction && (effectiveManualRoute.length > 0 || builderExpanded || !((results ?? []).some((r) => r.status === "found"))) && (
              <div className="mt-2">
                {/* Z files a VFR initial level (item 15a) — no top-level FL field; the IFR-portion level
                    is entered at the pick-up point. Y and pure IFR set their 15a level here. */}
                {flightRules !== "Z" && (
                  <div className="d-flex align-items-end gap-2 mb-2">
                    <div style={{ width: "90px" }}>
                      <div className="ifr-input-label">Flight level</div>
                      <input
                        className={"ifr-plan-input" + (levelErrorShown ? " invalid" : "")}
                        value={planFL}
                        placeholder="100"
                        maxLength={3}
                        inputMode="numeric"
                        onChange={(e) => {
                          setPlanFL(e.target.value.replace(/[^0-9]/g, ""));
                          //the complaint was about this field — drop it as soon as it is being fixed
                          if (levelErrorShown) setError(undefined);
                        }}
                      />
                    </div>
                    <div className="ifr-status-line" style={{ paddingBottom: "6px" }}>
                      {flightRules === "Y"
                        ? "Initial IFR level — the route is checked at this level until IFR is cancelled."
                        : "The route is checked at this level."}
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-baseline">
                  <div className="ifr-label mb-0">
                    Manual route (Field 15)
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <BsTooltip id="ifr-manual-route-help" className="ifr-help-tooltip">
                          Type idents — Tab completes, Enter commits, Backspace deletes before the caret. Click a pill
                          to move the caret there (←/→ also move it); × removes a token. Or tap fixes and airways on
                          the map. Checking validates with EUROCONTROL and lists the result below.
                        </BsTooltip>
                      }
                    >
                      <button type="button" className="ifr-info-btn" aria-label="How to build a route">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </button>
                    </OverlayTrigger>
                  </div>
                  <div>
                    <button className="ifr-link-btn me-2" onClick={toggleTextMode}>
                      {textMode ? "Back to pills" : "Edit as text"}
                    </button>
                    {effectiveManualRoute.length > 0 && (
                      <button className="ifr-link-btn" onClick={clearManualRoute}>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                {textMode ? (
                  <textarea
                    className="ifr-route-textarea mt-1"
                    value={textDraft}
                    spellCheck={false}
                    placeholder={routePlaceholder}
                    onChange={(e) => setTextDraft(e.target.value.toUpperCase())}
                  />
                ) : (
                  (() => {
                    //The editor IS the route: pills render around the caret input (head · caret · tail),
                    //airways swappable, gaps fillable, × removes — insertion happens wherever the caret sits.
                    const renderToken = (side: "head" | "tail") => (t: string, i: number) => {
                      const tokens = sideTokens(side);
                      //rules-change tokens ("SOSUS/N0120F080") classify by the point before the slash
                      const changeMatch = t.match(POINT_CHANGE_RE);
                      const lookup = changeMatch ? changeMatch[1] : t;
                      const known =
                        t === "DCT" || t === "IFR" || t === "VFR" || SPEED_LEVEL_RE.test(t)
                          ? "meta"
                          : FIX_BY_IDENT.has(lookup)
                          ? "fix"
                          : AIRWAYS_BY_IDENT.has(t)
                          ? "awy"
                          : cupByCode.has(lookup)
                          ? "apt"
                          : vfrByCode.has(lookup)
                          ? "vfr"
                          : "unknown";
                      const isOpen = openAirwayToken?.side === side && openAirwayToken.idx === i;

                      const tokenEl =
                        known === "awy" ? (
                          <button
                            className={"ifr-awy-pill" + (isOpen ? " open" : "")}
                            title="Tap to swap airway"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenAirwayToken(isOpen ? undefined : { side, idx: i });
                            }}
                          >
                            {t} ▾
                          </button>
                        ) : (
                          <span
                            className={"ifr-token " + known}
                            title="Click to move the caret after this token"
                            onClick={(e) => {
                              e.stopPropagation();
                              placeCaretAfterToken(side, i);
                            }}
                          >
                            {/* one chip, but shown with its real spaces ("EAST AIGINA") */}
                            {phraseToDisplay(t)}
                          </span>
                        );

                      //Two consecutive IFR fixes = an implicit DCT leg (rejected in Greek airspace) —
                      //offer the connecting airway chain as an insert pill, or an explicit DCT as last
                      //resort. Adjacency looks THROUGH the IFR marker and rules-change groups (the
                      //airway leg starts at the pick-up fix); a VFR marker ends the airway world.
                      let gapEl: JSX.Element | null = null;
                      let nextIdx = i + 1;
                      while (nextIdx < tokens.length && tokens[nextIdx] === "IFR") nextIdx++;
                      const nextTok = tokens[nextIdx];
                      const nextIdent = nextTok ? nextTok.match(POINT_CHANGE_RE)?.[1] ?? nextTok : undefined;
                      if (nextIdent && FIX_BY_IDENT.has(lookup) && FIX_BY_IDENT.has(nextIdent)) {
                        const segs = findNetworkPath(lookup, nextIdent);
                        if (segs) {
                          const chain = segs.flatMap((sg) => [airwayIdentForLevel(sg.awy), sg.exit]);
                          chain.pop(); //final exit fix already follows in the route
                          gapEl = (
                            <button
                              className="ifr-gap-pill"
                              title={`Insert ${chain.join(" ")}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                insertManualTokensAt(side, nextIdx, chain);
                              }}
                            >
                              +{chain.length === 1 ? ` ${chain[0]}` : ` ${chain[0]} +${chain.length - 1}`}
                            </button>
                          );
                        } else {
                          gapEl = (
                            <button
                              className="ifr-gap-pill dct"
                              title="No airway connection in the network — insert explicit DCT (Greek airspace usually rejects DCT legs)"
                              onClick={(e) => {
                                e.stopPropagation();
                                insertManualTokensAt(side, nextIdx, ["DCT"]);
                              }}
                            >
                              + DCT
                            </button>
                          );
                        }
                      }

                      //position of the caret AFTER this token, in absolute route-token coordinates
                      const globalAfter =
                        side === "head" ? i + 1 : manualCommitted.length + (manualPartial ? 1 : 0) + i + 1;
                      return (
                        <Fragment key={`${side}-${i}`}>
                          <span className="ifr-token-wrap" data-after={globalAfter}>
                            {tokenEl}
                            <button
                              className="ifr-token-x"
                              title={`Remove ${t}`}
                              aria-label={`Remove ${t}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeManualToken(side, i);
                              }}
                            >
                              ×
                            </button>
                          </span>
                          {gapEl}
                        </Fragment>
                      );
                    };
                    return (
                      <div className="ifr-route-editor mt-1" onClick={handleEditorClick}>
                        {manualCommitted.map(renderToken("head"))}
                        <input
                          key="ifr-caret-input"
                          ref={manualInputRef}
                          className={"ifr-route-editor-input" + (manualTailTokens.length > 0 ? " mid" : "")}
                          style={manualTailTokens.length > 0 ? { width: `${Math.max(4, manualPartial.length + 2)}ch` } : undefined}
                          value={manualPartial}
                          spellCheck={false}
                          placeholder={manualCommitted.length === 0 && manualTailTokens.length === 0 ? routePlaceholder : ""}
                          onChange={(e) => setManualRoute(manualCommittedPrefix + e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === "Tab" && routeSuggestions.length > 0) {
                              e.preventDefault();
                              acceptSuggestion(routeSuggestions[0]);
                            } else if (e.key === "Enter") {
                              e.preventDefault();
                              if (manualPartial.length > 0) setManualRoute(manualCommittedPrefix + manualPartial + " ");
                            } else if (e.key === "Backspace" && manualPartial.length === 0 && manualCommitted.length > 0) {
                              e.preventDefault();
                              const rest = manualCommitted.slice(0, -1);
                              setManualRoute(rest.join(" ") + (rest.length > 0 ? " " : ""));
                            } else if (e.key === "ArrowLeft" && manualPartial.length === 0 && manualCommitted.length > 0) {
                              //step the caret over the previous token
                              e.preventDefault();
                              setCaretTokens(manualCommitted.slice(0, -1), [manualCommitted[manualCommitted.length - 1], ...manualTailTokens]);
                            } else if (e.key === "ArrowRight" && manualPartial.length === 0 && manualTailTokens.length > 0) {
                              e.preventDefault();
                              setCaretTokens([...manualCommitted, manualTailTokens[0]], manualTailTokens.slice(1));
                            }
                          }}
                        />
                        {manualTailTokens.map(renderToken("tail"))}
                      </div>
                    );
                  })()
                )}
                {/* Airway swap panel — when open it OWNS this row: no autocomplete suggestions show,
                    we are only swapping the tapped airway pill. */}
                {!textMode &&
                  openAirwayToken !== undefined &&
                  (() => {
                    const { side, idx } = openAirwayToken;
                    const tokens = sideTokens(side);
                    if (idx >= tokens.length || !AIRWAYS_BY_IDENT.has(tokens[idx])) return null;
                    //alternatives are judged on the full token sequence, so context spans the caret
                    const all = [...manualCommitted, ...(manualPartial ? [manualPartial] : []), ...manualTailTokens];
                    const globalIdx = side === "head" ? idx : manualCommitted.length + (manualPartial ? 1 : 0) + idx;
                    const alts = airwayAlternatives(all, globalIdx);
                    return (
                      <div className="ifr-swap-panel mt-1">
                        <div className="d-flex flex-wrap gap-1 align-items-center">
                          <span className="ifr-swap-title">Swap airway {tokens[idx]} →</span>
                          {alts.length === 0 && <span className="ifr-status-line">no alternative airway for this leg</span>}
                          {alts.map((alt) => (
                            <button key={alt} className="ifr-swap-chip" onClick={() => swapManualToken(side, idx, alt)}>
                              {alt}
                            </button>
                          ))}
                          <button className="ifr-link-btn ms-auto" onClick={() => setOpenAirwayToken(undefined)}>
                            done
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                {!textMode && openAirwayToken === undefined && !pickupDraft && routeSuggestions.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {/* kind class is namespaced ("ifr-kind-nav", not "nav"): a bare kind would collide
                        with Bootstrap's .nav component class, whose padding-left:0 eats the chip's
                        left padding and knocks the label off-centre. */}
                    {routeSuggestions.map((sug) => (
                      <button key={sug.ident} className={"ifr-suggest-chip ifr-kind-" + sug.kind} onClick={() => acceptSuggestion(sug)}>
                        {/* multi-word names are held sentinel-joined; show them with real spaces */}
                        {phraseToDisplay(sug.ident)}
                        <span className="ifr-suggest-kind">
                          {sug.kind === "awy" ? "AWY" : sug.kind === "apt" ? "APT" : sug.kind === "vfr" ? "VFR" : sug.kind === "nav" ? "NAV" : "FIX"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* rules-change action, directly under the editor like the suggestions — only the
                    transition valid at the caret (VFR segment → pick up IFR, IFR segment → cancel IFR) */}
                {!textMode && (flightRules === "Y" || flightRules === "Z") && !pickupDraft && (
                  <div className="mt-1">
                    {caretInVfrSegment ? (
                      <button
                        className="ifr-rules-chip pickup"
                        title="Set the IFR pick-up point, speed and level (e.g. SOSUS/N0120F080 IFR)"
                        onClick={openIfrPickup}
                      >
                        + IFR pick-up
                      </button>
                    ) : (
                      <button
                        className="ifr-rules-chip cancel"
                        title="Cancel IFR at the point before the caret (e.g. AMALA VFR)"
                        onClick={insertVfrCancel}
                      >
                        + cancel IFR (go VFR)
                      </button>
                    )}
                  </div>
                )}

                {pickupDraft && (
                  <div className="rounded p-2 mt-2 mb-1" style={{ background: "#e2e6ea", border: "1px solid #adb5bd" }}>
                    <div className="d-flex flex-wrap gap-2 align-items-end">
                      <div style={{ width: "110px" }}>
                        <div className="ifr-input-label">Pick-up point</div>
                        <input
                          className="ifr-plan-input"
                          value={pickupDraft.point}
                          placeholder="SOSUS"
                          maxLength={10}
                          spellCheck={false}
                          onChange={(e) => setPickupDraft({ ...pickupDraft, point: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                        />
                      </div>
                      <div style={{ width: "84px" }}>
                        <div className="ifr-input-label">Speed kt</div>
                        <input
                          className="ifr-plan-input"
                          value={pickupDraft.speed}
                          placeholder="120"
                          maxLength={4}
                          inputMode="numeric"
                          onChange={(e) => setPickupDraft({ ...pickupDraft, speed: e.target.value.replace(/[^0-9]/g, "") })}
                        />
                      </div>
                      <div style={{ width: "70px" }}>
                        <div className="ifr-input-label">FL</div>
                        <input
                          className="ifr-plan-input"
                          value={pickupDraft.level}
                          placeholder="080"
                          maxLength={3}
                          inputMode="numeric"
                          onChange={(e) => setPickupDraft({ ...pickupDraft, level: e.target.value.replace(/[^0-9]/g, "") })}
                        />
                      </div>
                      <button
                        className="ifr-find-btn"
                        style={{ width: "auto", minWidth: "104px", padding: "0.45rem 1rem", fontSize: "0.9rem", lineHeight: 1.2 }}
                        onClick={confirmIfrPickup}
                      >
                        Insert
                      </button>
                      <button
                        className="ifr-ghost-btn"
                        style={{ minWidth: "104px", padding: "0.45rem 1rem", fontSize: "0.9rem", lineHeight: 1.2 }}
                        onClick={() => setPickupDraft(undefined)}
                      >
                        Cancel
                      </button>
                    </div>
                    {pickupFixSuggestions.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                        <span className="ifr-status-line">nearest IFR fixes:</span>
                        {pickupFixSuggestions.map((f) => (
                          <button key={f} className="ifr-suggest-chip" onClick={() => setPickupDraft({ ...pickupDraft, point: f })}>
                            {f}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="ifr-status-line mt-1">
                      Inserts <span className="ifr-mono">{pickupPreview}</span>
                    </div>
                  </div>
                )}
                {/* while adding an IFR pick-up, the pick-up editor owns the flow — no Check route yet */}
                {!pickupDraft && (
                  <button
                    className="ifr-ghost-btn w-100 mt-1"
                    onClick={handleCheckManual}
                    disabled={checkingManual || effectiveManualRoute.length === 0}
                  >
                    {checkingManual ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Checking…
                      </>
                    ) : (
                      "Check route"
                    )}
                  </button>
                )}
              </div>
            )}

            </div>

            {results && (mode === "find" || visibleOptions.length > 0) && (
              <div className="ifr-section">
                <div className="ifr-section-title">
                  {mode === "find" ? "Found routes" : "Checked routes"} {visibleOptions.length > 0 && `· ${visibleOptions.length}`}
                </div>

                {visibleOptions.length === 0 && !finding && (
                  <div className="ifr-idle">No routes found — adjust profiles, levels or plan data</div>
                )}

                {visibleOptions.map((option) => {
                  const r = option.result;
                  const idx = options.indexOf(option); //index in the full list keeps colors stable across modes
                  const modes = option.optimizes;
                  const valid = ifpuvIsValid(r.validation);
                  const isExpanded = expandedKey === option.key;
                  const wptsOpen = expandedWaypoints[option.key] ?? false;
                  const isSelected = selectedField15 === r.field15;
                  const isFocused = focusedKey === option.key;
                  const color = colorForOption(option, idx);
                  //validated under different planning inputs (DEP/DEST/ALTN/date changed since)
                  const outdated = (optionSignatures[option.key] ?? planningSignature) !== planningSignature;
                  const metrics = [
                    r.routeDist !== undefined ? `${Math.round(r.routeDist)} NM` : undefined,
                    formatSecondsAsHHMM(r.routeTime),
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const toggleExpanded = () => {
                    setPinnedKey(option.key);
                    setExpandedKey(isExpanded ? undefined : option.key);
                  };
                  return (
                    <div
                      key={option.key}
                      className={"ifr-strip" + (isFocused ? " focused" : "") + (isSelected ? " armed" : "") + (isExpanded ? " expanded" : " collapsed")}
                      style={{ ["--strip-color" as any]: color }}
                      onMouseEnter={() => setHoverKey(option.key)}
                      onMouseLeave={() => setHoverKey(undefined)}
                    >
                      {/* one-line summary — the whole row toggles the details open/closed */}
                      <div className="ifr-strip-head" onClick={toggleExpanded}>
                        <span className="ifr-strip-label" style={{ color }}>
                          {modes.map(optimizeLabel).join(" · ")}
                          {flLabel(r.level) ? ` · ${flLabel(r.level)}` : ""}
                        </span>
                        {outdated ? (
                          <span
                            className="ifr-annunciator sm warn"
                            title="Departure/destination, alternates or off-block time changed since this was checked — re-check the route"
                          >
                            outdated
                          </span>
                        ) : valid ? (
                          <span className="ifr-annunciator sm ok">OK</span>
                        ) : (
                          <span className="ifr-annunciator sm err">IFPUV</span>
                        )}
                        {r.delays && r.delays.length > 0 && <span className="ifr-annunciator sm warn">ATFM</span>}
                        {isSelected && (
                          <span className="ifr-annunciator sm armed">
                            <FontAwesomeIcon icon={faCheck} style={{ fontSize: "0.55rem" }} /> Armed
                          </span>
                        )}
                        <span className="ifr-strip-metrics">{metrics}</span>
                        <span className="ifr-strip-chevron">▾</span>
                        {option.optimizes.includes("manual") && !isSelected && (
                          <button
                            className="ifr-strip-x"
                            title="Remove from list"
                            aria-label="Remove checked route"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCheckedOption(option);
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {!isExpanded && (
                        <div className="ifr-route-mini" onClick={toggleExpanded} title={r.field15}>
                          {r.field15}
                        </div>
                      )}

                      {isExpanded && (
                        <>
                      <div className="ifr-route-text">{r.field15}</div>

                      <div className="ifr-readouts">
                        {r.routeDist !== undefined && (
                          <div className="ifr-readout">
                            <div className="ifr-readout-label">Dist</div>
                            <div className="ifr-readout-value">{Math.round(r.routeDist)} NM</div>
                          </div>
                        )}
                        {r.routeTime !== undefined && (
                          <div className="ifr-readout">
                            <div className="ifr-readout-label">EET</div>
                            <div className="ifr-readout-value">{formatSecondsAsHHMM(r.routeTime)}</div>
                          </div>
                        )}
                        {r.routeFuel !== undefined && (
                          <div className="ifr-readout">
                            <div className="ifr-readout-label">Fuel</div>
                            <div className="ifr-readout-value">{Math.round(r.routeFuel)}</div>
                          </div>
                        )}
                        {r.initialAltitude !== undefined && (
                          <div className="ifr-readout">
                            <div className="ifr-readout-label">Init</div>
                            <div className="ifr-readout-value">{r.initialAltitude}</div>
                          </div>
                        )}
                      </div>

                      {!valid && r.validation && <div className="ifr-validation-errors">{r.validation}</div>}

                      {(r.delays ?? []).map((d, i) => {
                        const band = delayBand(d.code);
                        const affects = delayAffectsFlight(d, departureDateTime, r.routeTime);
                        return (
                          <span key={i} className={"ifr-delay-chip" + (band.minor ? " band-minor" : "")}>
                            <span className="ifr-delay-title">⚠ ATFM · {band.label}</span>
                            <br />
                            {d.locationIdent ?? "?"}
                            {d.airspace ? ` (${d.airspace})` : ""}
                            {d.regulations && d.regulations.length > 0 ? ` · reg ${d.regulations.join(", ")}` : ""}
                            {formatEpochWindow(d.startTime, d.endTime) ? ` · ${formatEpochWindow(d.startTime, d.endTime)}` : ""}
                            {affects !== undefined && (
                              <>
                                {" · "}
                                <span className={affects ? "ifr-delay-hit" : "ifr-delay-miss"}>
                                  {affects ? "affects your ETD" : "outside your times"}
                                </span>
                              </>
                            )}
                          </span>
                        );
                      })}

                      {(() => {
                        //Mixed (Y/Z) routes: autorouter's waypoint list is IFR-centric and mangles the
                        //VFR legs (blank names for reporting points, spurious airway re-routing), so show
                        //the ACTUAL Field-15 route points instead, tagged VFR/IFR by segment.
                        const mixedLegs = isMixedField15(r.field15) ? field15Legs(r.field15) : [];
                        if (mixedLegs.length >= 2) {
                          return (
                            <div className="mt-2">
                              <button
                                className="ifr-link-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedWaypoints((prev) => ({ ...prev, [option.key]: !wptsOpen }));
                                }}
                              >
                                {wptsOpen ? "Hide" : "Show"} waypoints · {mixedLegs.length}
                              </button>
                              {wptsOpen && (
                                <div className="mt-1">
                                  {mixedLegs.map((p, i) => (
                                    <div key={i} className="ifr-wpt-row">
                                      <span>{p.ident}</span>
                                      <span style={{ color: p.ifr === false ? "#087990" : "#b26a00", fontWeight: 600 }}>
                                        {p.ifr === false ? "VFR" : "IFR"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (!r.waypoints || r.waypoints.length === 0) return null;
                        return (
                          <div className="mt-2">
                            <button
                              className="ifr-link-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedWaypoints((prev) => ({ ...prev, [option.key]: !wptsOpen }));
                              }}
                            >
                              {wptsOpen ? "Hide" : "Show"} waypoints · {r.waypoints.length}
                            </button>
                            {wptsOpen && (
                              <div className="mt-1">
                                {r.waypoints.map((w, i) => (
                                  <div key={i} className="ifr-wpt-row">
                                    <span>
                                      {w.ident ?? w.name}
                                      {w.airway ? <span className="text-dim"> · {w.airway}</span> : ""}
                                    </span>
                                    <span className="text-dim">
                                      {w.dist !== undefined ? `${Math.round(w.dist)} NM` : ""}
                                      {w.altitude !== undefined ? ` · ${w.altitude}` : ""}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* A saved route template must be IFPS-clean by construction: only a route that
                          passed IFPUV can be armed, so the route that reaches "Validate & Save" is
                          already validated and never has to be re-checked. A flight may still arm a
                          failing route — its errors are often FPL-level (equipment/PBN) rather than the
                          route itself, and filing stays blocked by the IFPS gate until they are fixed.
                          Disarming an armed option is always allowed. */}
                      <button
                        className={"ifr-use-btn" + (isSelected ? " armed" : "")}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSelected) handleDisarm();
                          else handleSelect(r.field15);
                        }}
                        disabled={selectingField15 !== undefined || (!isSelected && entityKind === "route" && !valid)}
                        title={!isSelected && entityKind === "route" && !valid ? "IFPS rejected this route — fix the errors above, or check another route." : undefined}
                      >
                        {selectingField15 === r.field15 || (isSelected && selectingField15 === "__disarm__") ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            {isSelected ? "Disarming…" : "Arming…"}
                          </>
                        ) : isSelected ? (
                          <>
                            <FontAwesomeIcon icon={faCheck} className="me-1" /> Armed — tap to disarm
                          </>
                        ) : entityKind === "route" && !valid ? (
                          "IFPS errors — cannot save this route"
                        ) : (
                          "Use this route"
                        )}
                      </button>
                      {checkManualRouteAction && (
                        <button
                          className="ifr-link-btn mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMode("manual");
                            setTextMode(false);
                            setManualTail("");
                            setTextDraft("");
                            //re-join multi-word VFR names so each returns as ONE pill, and add a trailing
                            //space so every token commits (otherwise the last renders as still being typed)
                            setManualRoute(joinKnownPhrases(r.field15.trim().toUpperCase(), vfrPhrases) + " ");
                          }}
                        >
                          Copy to manual box for editing
                        </button>
                      )}
                        </>
                      )}
                    </div>
                  );
                })}

                {(pendingCount > 0 || failedCount > 0) && (
                  <div className="ifr-status-line mt-1">
                    {pendingCount > 0 && `${pendingCount} run(s) still computing. `}
                    {failedCount > 0 && `${failedCount} run(s) failed.`}
                  </div>
                )}
              </div>
            )}

            {!results && !finding && mode === "find" && (
              <div className="ifr-idle mt-3">Select profiles and find routes — candidates render on the chart</div>
            )}

            {validateFplAction && (
              <div className="mt-3">
                <button className="ifr-ghost-btn" onClick={handleValidate} disabled={validating}>
                  {validating ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Validating…
                    </>
                  ) : (
                    "Re-validate current FPL"
                  )}
                </button>
                {validation && (
                  <div className="mt-2">
                    {validation.result === "ok" ? (
                      <span className="ifr-annunciator ok">No errors — IFPS valid</span>
                    ) : (
                      <div className="ifr-validation-errors">
                        {validation.errors.length > 0 ? validation.errors.join("\n") : validation.validation}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            </>
            )}

            {error && <div className="ifr-validation-errors mt-3">{error}</div>}
          </div>

          <div className="p-3" style={{ borderTop: "1px solid var(--ifr-border)" }}>
            <button className={selectedField15 ? "ifr-find-btn" : "ifr-ghost-btn w-100"} onClick={requestClose}>
              {selectedField15 ? (persistedOnSelect ? "Done — ready to file" : "Done") : "Close"}
            </button>
          </div>
        </div>

        {/* ------------- map ------------- */}
        <div className="ifr-map-pane">
          <MapContainer ref={mapRef} center={[38.5, 24]} zoom={7} scrollWheelZoom maxZoom={18} zoomControl>
            <MapSizeFix />
            <FitToRoutes options={visibleOptions.map((o) => o.result)} disabled={draftRoute.length >= 2} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Open Flight Maps">
                <TileLayer
                  attribution='&copy; <a href="https://www.openflightmaps.org/">Open Flight Maps</a>'
                  url="https://openflightmaps.avitracer.com/112022/{z}/{x}/{y}.png"
                  maxNativeZoom={11}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellite Hybrid">
                <TileLayer url="https://api.mapbox.com/styles/v1/avitracer/cl4k53v3e001x14lkhn445epj/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiYXZpdHJhY2VyIiwiYSI6ImNsZ3Q3ajU0dzEwcGozZW8yZ2NtNGptd28ifQ.3cjrtycY3_QvWjr_XZdiVQ" />
              </LayersControl.BaseLayer>
              <LayersControl.Overlay name="Airports">
                <LayerGroup><VisibleCupPoints points={navPointsByLayer.airports}>{renderNavMarker}</VisibleCupPoints></LayerGroup>
              </LayersControl.Overlay>
              <LayersControl.Overlay name="VOR / NDB">
                <LayerGroup>
                  <VisibleCupPoints points={navPointsByLayer.navaids}>{renderNavMarker}</VisibleCupPoints>
                </LayerGroup>
              </LayersControl.Overlay>
              <LayersControl.Overlay checked name="IFR Fixes">
                <LayerGroup>
                  <VisibleCupPoints points={vfrLayerVisible ? navPointsByLayer.ifrFixes : [...navPointsByLayer.ifrFixes, ...navPointsByLayer.vfrIfrFixes]}>{renderNavMarker}</VisibleCupPoints>
                </LayerGroup>
              </LayersControl.Overlay>
              <LayersControl.Overlay checked={airwaysLayerVisible} name="Airways (ENR 3)">
                <LayerGroup>
                  {AIRWAY_SEGMENTS.map((seg, i) => (
                    <Polyline
                      key={`awy-${seg.airway.idents[0]}-${i}`}
                      positions={[[seg.from.lat, seg.from.lon], [seg.to.lat, seg.to.lon]]}
                      pathOptions={{ color: "#7188a8", weight: 2, dashArray: "6 6", opacity: 0.7 }}
                      eventHandlers={
                        checkManualRouteAction
                          ? { click: () => appendAirwaySegment(seg) }
                          : undefined
                      }
                    >
                      <Tooltip sticky className="ifr-wpt-tooltip">
                        {seg.airway.idents.join("/")} · {seg.from.ident} → {seg.to.ident}
                        {checkManualRouteAction ? " · click to add" : ""}
                      </Tooltip>
                    </Polyline>
                  ))}
                  {AIRWAY_FIXES.map((f) => (
                    <Marker
                      key={`fix-${f.ident}`}
                      position={[f.lat, f.lon]}
                      icon={Leaflet.divIcon({ className: "ifr-fix-icon", html: "▲", iconSize: [18, 18], iconAnchor: [9, 9] })}
                      eventHandlers={checkManualRouteAction ? { click: () => handleFixTap(f.ident) } : undefined}
                    >
                      <Tooltip className="ifr-wpt-tooltip" direction="top" offset={[0, -8]}>
                        {f.ident}
                        {pickupDraft ? " · tap to set as IFR pick-up" : checkManualRouteAction ? " · click to add" : ""}
                      </Tooltip>
                    </Marker>
                  ))}
                </LayerGroup>
              </LayersControl.Overlay>
              {/* VFR reporting points — user-toggleable, and auto-shown while composing a VFR segment
                  (see the vfrLayerVisible effect). Mixed routes start with it on. */}
              <LayersControl.Overlay name="VFR Reporting points" checked={vfrLayerVisible}>
                <LayerGroup>
                  <VisibleCupPoints points={navPointsByLayer.reportingPoints}>{renderNavMarker}</VisibleCupPoints>
                </LayerGroup>
              </LayersControl.Overlay>
            </LayersControl>
            <ZoomToSeePointsHint />

            {/* keep React's vfrLayerVisible in sync when the user toggles the layer from the control,
                so the auto-effect and the manual toggle don't fight each other */}
            <OverlayToggleSync name="VFR Reporting points" onChange={(on) => setVfrLayerVisible(on)} />
            <OverlayToggleSync name="Airways (ENR 3)" onChange={(on) => setAirwaysLayerVisible(on)} />

            {/* ATFM regulation areas — amber caution zones, deeper amber for heavier delay bands */}
            {delayOverlays.flatMap((d, i) => {
              const band = delayBand(d.code);
              const color = band.minor ? "#ffc107" : "#fd7e14";
              return geometryPolygons(d.geometry).map((rings, j) => (
                <Polygon
                  key={`atfm-${d.locationIdent}-${d.startTime}-${i}-${j}`}
                  positions={rings}
                  pathOptions={{ color, weight: 2, dashArray: "6 6", fillColor: color, fillOpacity: 0.15 }}
                >
                  <Tooltip sticky className="ifr-wpt-tooltip">
                    ATFM {band.label} · {d.locationIdent ?? ""} {d.regulations?.join(", ") ?? ""}{" "}
                    {formatEpochWindow(d.startTime, d.endTime) ?? ""}
                  </Tooltip>
                </Polygon>
              ));
            })}

            {/* candidate routes — only the focused and the armed one draw; the rest stay as rows
                (hover a row to see its line) to keep the chart readable. Mixed (Y/Z) routes keep the
                per-leg VFR-green / IFR-yellow coloring from the builder instead of one flat colour. */}
            {visibleOptions.map((option) => {
              const isFocused = focusedKey === option.key;
              const isSelected = selectedField15 === option.result.field15;
              if (!isFocused && !isSelected) return null;
              //A route IFPS rejected is not flyable — drawing it suggests an option that does not exist.
              //An ARMED one still draws: on a flight the user may knowingly arm a plan whose errors are
              //FPL-level (equipment/PBN), and hiding the route they just chose would be worse.
              if (!isSelected && !ifpuvIsValid(option.result.validation)) return null;
              const positions = waypointPositions(option.result.waypoints);
              if (positions.length < 2) return null;
              const color = colorForOption(option, options.indexOf(option));
              const handlers = {
                click: () => setPinnedKey(option.key),
                mouseover: () => setHoverKey(option.key),
                mouseout: () => setHoverKey(undefined),
              };

              //Mixed rules: color each leg by the rules it is flown under (green VFR / yellow IFR).
              const legs = isMixedField15(option.result.field15) ? field15Legs(option.result.field15) : [];
              if (legs.length >= 2) {
                return (
                  <Fragment key={option.key}>
                    {legs.slice(1).map((to, i) => {
                      const from = legs[i];
                      return (
                        <Polyline
                          key={`${option.key}-leg-${i}`}
                          positions={[[from.lat, from.lon], [to.lat, to.lon]]}
                          pathOptions={{ color: to.ifr === false ? "#35d27a" : "#ffb02e", weight: 5, opacity: 0.95 }}
                          eventHandlers={handlers}
                        />
                      );
                    })}
                  </Fragment>
                );
              }

              return (
                <Polyline
                  key={option.key}
                  positions={positions}
                  pathOptions={{ color, weight: 5, opacity: 0.95 }}
                  eventHandlers={handlers}
                />
              );
            })}

            {/* marching dashes along the armed route — for mixed (Y/Z) routes follow the WHOLE
                Field-15 (autorouter's waypoints only cover the IFR portion), else the waypoints. */}
            {(() => {
              const armed = options.find((o) => o.result.field15 === selectedField15);
              if (!armed) return null;
              const positions = isMixedField15(armed.result.field15)
                ? field15Legs(armed.result.field15).map((p) => [p.lat, p.lon] as LatLngTuple)
                : waypointPositions(armed.result.waypoints);
              if (positions.length < 2) return null;
              return (
                <Polyline
                  positions={positions}
                  pathOptions={{ color: "#ffffff", weight: 2.5, opacity: 0.95, className: "ifr-route-armed-dash" }}
                  interactive={false}
                />
              );
            })()}

            {/* DEP / DEST / ALTN aerodromes from the planning inputs */}
            {aerodromeMarkers.map((a) => (
              <CircleMarker
                key={`aero-${a.role}-${a.code}`}
                center={[a.point.lat, a.point.lon]}
                radius={9}
                pathOptions={{ color: a.color, weight: 3, fillColor: "#0d131c", fillOpacity: 0.85 }}
                interactive={false}
              >
                <Tooltip permanent direction="top" offset={[0, -10]} className="ifr-wpt-tooltip">
                  {a.role} {a.code}
                </Tooltip>
              </CircleMarker>
            ))}

            {/* live draft of the manual-route text being composed — high contrast, with DEP/DEST context.
                Rendered per-leg: IFR legs solid yellow, VFR legs of mixed (Y/Z) routes solid green. */}
            {draftRoute.length >= 2 &&
              draftRoute.slice(1).map((to, i) => {
                const from = draftRoute[i];
                const color = to.ifr === false ? "#35d27a" : "#ffe14d";
                return (
                  <Fragment key={`draft-leg-${i}`}>
                    <Polyline
                      positions={[[from.lat, from.lon], [to.lat, to.lon]]}
                      pathOptions={{ color: "#0d131c", weight: 7, opacity: 0.55 }}
                      interactive={false}
                    />
                    <Polyline
                      positions={[[from.lat, from.lon], [to.lat, to.lon]]}
                      pathOptions={{ color, weight: 3.5, opacity: 1 }}
                      interactive={false}
                    />
                  </Fragment>
                );
              })}
            {(() => {
              //The two aerodrome legs are NOT equivalent, so they are not drawn the same:
              //  DEP -> first point is genuinely flown as filed (a direct from the departure aerodrome),
              //    so it is drawn solid, exactly like a typed leg — the line runs unbroken from the field.
              //  last point -> DEST is only an indication of where the route is heading; the actual track
              //    in is the arrival/approach, not this straight line, so it stays dashed.
              //Both keep the dark casing every leg has — without it a light line vanishes on the chart.
              //Colour follows the adjacent point's rules, so a mixed (Y/Z) route leaves in green.
              if (draftRoute.length === 0) return null;
              const dep = aerodromeMarkers.find((a) => a.role === "DEP");
              const dest = aerodromeMarkers.find((a) => a.role === "DEST");
              const first = draftRoute[0];
              const last = draftRoute[draftRoute.length - 1];
              const depColor = first.ifr === false ? "#35d27a" : "#ffe14d";
              return (
                <>
                  {dep && (
                    <Fragment key="draft-connector-dep">
                      <Polyline
                        positions={[[dep.point.lat, dep.point.lon], [first.lat, first.lon]]}
                        pathOptions={{ color: "#0d131c", weight: 7, opacity: 0.55 }}
                        interactive={false}
                      />
                      <Polyline
                        positions={[[dep.point.lat, dep.point.lon], [first.lat, first.lon]]}
                        pathOptions={{ color: depColor, weight: 3.5, opacity: 1 }}
                        interactive={false}
                      />
                    </Fragment>
                  )}
                  {dest && (
                    <Polyline
                      positions={[[last.lat, last.lon], [dest.point.lat, dest.point.lon]]}
                      pathOptions={{ color: "#ffe14d", weight: 3, dashArray: "8 10", opacity: 0.5 }}
                      interactive={false}
                    />
                  )}
                </>
              );
            })()}
            {draftRoute.map((p, i) => (
              <CircleMarker
                key={`draft-${p.ident}-${i}`}
                center={[p.lat, p.lon]}
                radius={5}
                pathOptions={{ color: "#0d131c", weight: 2, fillColor: "#ffe14d", fillOpacity: 1 }}
                interactive={false}
              >
                <Tooltip className="ifr-wpt-tooltip" direction="top" offset={[0, -6]}>
                  {phraseToDisplay(p.ident)}
                </Tooltip>
              </CircleMarker>
            ))}
            <FitToDraft
              draft={draftRoute}
              //only DEP/DEST frame the view — alternates (e.g. LGAV) can be far away and would
              //blow the bounds up to cover half the country
              extra={aerodromeMarkers
                .filter((a) => a.role === "DEP" || a.role === "DEST")
                .map((a) => [a.point.lat, a.point.lon] as LatLngTuple)}
              active={draftRoute.length >= 2}
              //what may re-frame the view: the endpoints, or a whole route being armed — never the
              //individual points as they are added
              fitKey={`${planDep}>${planDest}#${selectedField15 ?? ""}`}
            />

            {/* waypoints of the focused route — hidden for an IFPS-rejected option, so its dots don't
                linger after its line is suppressed above */}
            {focusedOption &&
              (ifpuvIsValid(focusedOption.result.validation) || selectedField15 === focusedOption.result.field15) &&
              focusedOption.result.waypoints
                .filter((w) => w.lat !== undefined && w.lon !== undefined)
                .map((w, i, arr) => {
                  const isEnd = i === 0 || i === arr.length - 1;
                  return (
                    <CircleMarker
                      key={`${w.ident ?? w.name}-${i}`}
                      center={[w.lat!, w.lon!]}
                      radius={isEnd ? 7 : 4}
                      pathOptions={{
                        color: i === 0 ? "#35d27a" : i === arr.length - 1 ? "#ff5964" : "#ffffff",
                        weight: 2,
                        fillColor: "#0d131c",
                        fillOpacity: 1,
                      }}
                    >
                      <Tooltip className="ifr-wpt-tooltip" direction="top" offset={[0, -6]}>
                        {w.ident ?? w.name}
                        {w.airway ? ` · ${w.airway}` : ""}
                        {w.altitude !== undefined ? ` · ${w.altitude}` : ""}
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
          </MapContainer>

          {/* No per-route legend: only one route is ever drawn, so naming it added nothing — and it
              listed routes that were deliberately NOT drawn (an option rejected by IFPS still produced
              a legend row). The ATFM key stays: the coloured airspace overlays are not self-evident. */}
          {delayOverlays.length > 0 && (
            <div className="ifr-map-legend">
              {delayOverlays.some((d) => !delayBand(d.code).minor) && (
                <div className="ifr-legend-row">
                  <span className="ifr-legend-swatch" style={{ background: "#ffb02e" }} />
                  <span>ATFM delay &lt; 45 min</span>
                </div>
              )}
              {delayOverlays.some((d) => delayBand(d.code).minor) && (
                <div className="ifr-legend-row">
                  <span className="ifr-legend-swatch" style={{ background: "#5db9ff" }} />
                  <span>ATFM delay &lt; 15 min</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal show={showCloseConfirm} onHide={() => setShowCloseConfirm(false)} centered className="ifr-stacked-modal" backdropClassName="ifr-stacked-backdrop">
        <Modal.Header closeButton>
          <Modal.Title>Route disarmed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You disarmed the route this plan opened with. Closing now leaves it without a route.
          <div className="font-monospace small border rounded p-2 bg-light mt-2">{initialArmedRoute.current}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => { setShowCloseConfirm(false); onHide(); }}>
            Discard the route
          </Button>
          <Button variant="dark" onClick={revertAndClose}>
            Keep the original route
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}

// Reports user toggles of a named layers-control overlay back to React state, so a controlled
// `checked` prop and a manual toggle stay in sync instead of overriding each other.
// Leaflet initializes while the modal is still animating in — re-measure once settled.
function MapSizeFix() {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 200);
    const t2 = setTimeout(() => map.invalidateSize(), 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map]);
  return null;
}

//While composing (before any found options), keep the draft + aerodromes in view.
//Re-frames the map on the draft route. It deliberately keys on `fitKey` — the endpoints and the armed
//route — and NOT on the draft's points: while a route is being built point by point, every added
//waypoint would otherwise zoom the map out from under the user, exactly when they are trying to click
//the next one. Re-framing belongs to the events that change what the route IS: DEP/DEST changing, or a
//whole route being armed.
function FitToDraft({ draft, extra, active, fitKey }: { draft: DraftPoint[]; extra: LatLngTuple[]; active: boolean; fitKey: string }) {
  const map = useMap();
  useEffect(() => {
    if (!active || draft.length < 2) return;
    //Include the aerodromes so DEP/DEST context is in view, but bound the fit primarily on the route.
    const pts: LatLngTuple[] = [...draft.map((p) => [p.lat, p.lon] as LatLngTuple), ...extra];
    map.fitBounds(new Leaflet.LatLngBounds(pts), { padding: [30, 30], maxZoom: 11 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
}

function FitToRoutes({ options, disabled }: { options: IfrRouteFound[]; disabled?: boolean }) {
  const map = useMap();
  const signature = options.map((o) => o.routeId).join("|");
  useEffect(() => {
    if (disabled) return;
    const pts = options.flatMap((o) => waypointPositions(o.waypoints));
    if (pts.length > 1) {
      map.fitBounds(new Leaflet.LatLngBounds(pts), { padding: [30, 30], maxZoom: 11 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, map]);
  return null;
}

export default PlanIfrRouteModal;
